import {
  addFlowStatusFromCause,
  createAddFlowDiagnostic,
  type AddFlowOperation,
  type AddFlowOutcome
} from '#~/agent/components/bridge/add-flow-diagnostic.js';
import { broker } from '#~/agent/index.js';
import { useAgents } from '#~/agent/hooks.js';
// Renderer reload primitives mirrored from single-add's
// `credentials-dialog.tsx::handleConfirm` — after a successful
// Iris-enabled link the studio UI must reload so the just-joined
// agent's state-synced devices render in the sidebar. The bulk
// pipeline fires both calls AT MOST ONCE per commit (after the modal
// closes) when `irisLinkedCount > 0` — Iris-enabled UX consistency.
import { ElectronAPI } from '#~/electron/api.js';
import { isElectronApp } from '#~/electron/utils.js';
// `EVENT_NAMES` only — bulk-add emits via `emitMultiAddAnalytics` (takes
// the event name); `EVENT_CATEGORIES` is unused here so it is dropped to
// satisfy noUnusedLocals. Merge-integrated alongside main's
// `createDeviceLifecycleTracker` + embedded-flow analytics. Per
// `stay-in-scope` (ai.knowledge/global).
import { EVENT_NAMES } from '#~/analytics/constants/index.js';
import {
  createDeviceLifecycleTracker,
  isEmbeddedDiscovery,
  resolveEmbeddedFlowProps,
  type DeviceAddSuccessProperties
} from '#~/analytics/domains/index.js';
import { resolveDeviceAnalyticsCatalogContextFromModel } from '#~/analytics/device-context.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
// Analytics foundation. Every emit site routes through this helper so
// tests mock one symbol and the catalog stays the single source of
// truth for event names + properties.
// Per `reference-rules-in-comments` (ai.knowledge/global).
// `mapLinkOutcome` / `mapProbeOutcome` are no longer used here — the
// Iris-link and ONVIF retry-prompt paths they annotated were removed in
// Multi-Add Phase 2 (spec §3: those branches now record a placeholder).
import {
  emitMultiAddAnalytics,
  type MultiAddEmitProps
} from '#~/analytics/utils/emit-multi-add-analytics.js';
import { remainingCameraSlots } from '#~/api/billing/limits.js';
import { useAuth0 } from '#~/auth/index.js';
import { depot, type State as DepotState } from '#~/depot/index.js';
import { INTERCOM_EVENTS, trackIntercomEvent } from '#~/intercom/index.js';
import { ONBOARDING_PROPERTIES } from '#~/analytics/constants/onboarding-events.js';
import { logger } from '#~/logger/index.js';
import {
  describeAddDiscovery,
  describeAddFailure,
  describeBridgeDevice,
  describeDriverTemplate,
  traceCameraAdd
} from '#~/device/add-trace.js';
import { post } from '#~/notifications/utils.js';
// Origin registration helpers.
// `registerMultiAddOrigin` is the load-bearing call site: only
// `runBulkCommit` (not Wizard / single-add) may call it.
// Per `stay-in-scope` (ai.knowledge/global): wizard path never touches
// the multi-add origin slice — the boundary is load-bearing to keep
// the wizard regression from re-appearing.
import { registerMultiAddOrigin } from '#~/notifications/index.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import { buildSTagSelector } from '#~/stag/query.js';
import type { Api } from '@studio/design-system/dialog';
import { usePrompt } from '@studio/design-system/prompt';
import { ensureNotNil } from '@studio/js-utils';
import type { Agent } from '@studio/models/agent';
import { generateId } from '@studio/models';
import type { AppId } from '@studio/models/app';
import { filterTransients, type Brand, type Device, type Model } from '@studio/models/device';
import type { Collection } from '#~/api/device/registry/index.js';
import { recordTypedCamera } from '#~/api/device/registry/typed-camera.js';
import { getTemplate } from '#~/device/registry/commands/get-template.js';
import type { Room } from '@studio/models/room';
import type { Template } from '#~/device/registry/index.js';
import type { BulkAddStatus } from './store/types.js';
import { isOnvifDiscovery, probeOnvifDevice } from './source/onvif-probe.js';
import { isIrisDiscovery, linkIrisDevice } from './source/link-iris.js';
import { useLatestFn, useLazyRef, useMount, χref } from '@studio/react-utils/hooks';
import { defaults, isEqual, pick } from 'lodash-es';
import type { RefObject } from 'react';
import { useContext, useEffect, useMemo, useRef } from 'react';
// The RAW store, for reading state that was written moments ago. `useStore`
// hands back the last RENDER's snapshot, which is stale for a commit triggered
// in the same tick as the write that prepared it — see handleCommit.
import { isDiscoveryInUse } from './store/utils.js';
import type { Device$Video } from '#~/device/index.js';
import { Context as StoreContext } from './store/context.js';
import { shallow } from 'zustand/shallow';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { BulkCommitResult, CommitOptions, Wiring } from './context.js';
import { Context } from './context.js';
import { useBulkDiscardConfirmation } from './source/selector.js';
import {
  buildBrandDetectedCandidate,
  buildBulkCandidate,
  buildKnownModelCandidate
} from './source/utils.js';
import { useProducer, useStore } from './store/hooks.js';
import { addDevice, removePreview } from './store/utils.js';
import {
  abandonPreview,
  PREVIEW_CLEANUP_DEADLINE_MS,
  settlePreviewSetup
} from './store/mediator.js';
import { classifyPreviewPrepareCause } from './store/preview-outcome.js';
import { Panel } from './store/types.js';
import type { FinalSave, State } from './store/types.js';
// Failed-device slice mutations. Used by `handleBulkCommit` to reset
// the slice at the start of every batch and to record per-device
// failures inside `runBulkCommit`.
import { addFailedDevice, clearFailedDevices } from './store/failed-devices.slice.js';
import { resetIdentification } from './store/identification.slice.js';
import type { BulkSavePrepared } from './identification/bulk.js';
import {
  resolveBulkAddIdentification,
  resolveSingleAddIdentification,
  toPersistedIdentification
} from './identification/save.js';
// Cancellation slice mutations.
// `handleBulkCommit` clears the flag at the start of every batch; the
// React hook also subscribes to the live flag via the store `getState()`
// to power the deps `cancellationRequested` getter.
import { clearCancellation } from './store/bulk-add.slice.js';
// Auto-configured field slice mutator — Multi-Add Phase 2.
// `useWorkflow.handleBulkCommit` wires `recordAutoConfigured` to a closure
// that dispatches through `depot.getState().produce(...)` to write each
// `(deviceId, fieldPath, value)` triple into `depot.autoConfiguredFields`.
// The single-add `handleCommit` below uses the same mutator after
// `addDevice` resolves (the only point at which the freshly-generated
// device id is authoritative), keyed by the single-add pure compute helpers.
// Per `reference-rules-in-comments` (ai.knowledge/global).
import { addAutoConfiguredField } from '#~/device/store/auto-configured-fields.slice.js';
// Single-add pure helpers — used by single-add `handleCommit` to compute
// the source-derived field set without re-deriving the discriminator logic
// at the recording site. Same shape as the multi-add seam at runBulkCommit
// plan-build time. `isOnvifDiscovery` is already imported above for the
// runBulkCommit ONVIF branch; reused here.
import {
  computeKnownModelAutoConfigured,
  computeOnvifCapabilitiesAutoConfigured,
  resolveModelLessDeviceName
} from './source/utils.js';
import type { Discovery } from '#~/device/discovery/index.js';
// Transitory-placeholder slice (W2, spec §2). Bulk-add no longer drives a
// blocking auth modal for needs-creds ONVIF / Iris cameras (spec §0): it
// records a frontend-only placeholder so the tile + "Authenticate" chip
// render immediately and auth happens later via Advanced Settings (§3/§4).
import {
  setTransitoryDevice,
  type TransitoryKind,
  type TransitoryPlaceholder
} from '#~/device/transitory.js';
// Pure `Discovery → TransitoryPlaceholder` builder (host/port derivation).
import { buildPlaceholderForDiscovery } from './source/transitory-from-discovery.js';
import { pendingCameraHostCandidates, persistPendingCamera } from '#~/device/commands/pending.js';
import { Protocol as VideoProtocol } from '@studio/models/video';
import { discoveryDeviceAddProperties, resolveVideoStreamProps } from './device-add-analytics.js';

/** @internal */
export const useWiring = (): Wiring => ensureNotNil(useContext(Context));

/**
 * Modal-close dwell floor (ms) — mirrors the footer CTA dwell so the
 * user always sees the bulk-mode "Adding…" morph for at least this
 * long, even when `Promise.allSettled` resolves faster (e.g. mocked /
 * NATS-cached calls).
 *
 * Modal close fires at MAX(allSettled, 300ms).
 * Per `reference-rules-in-comments` (ai.knowledge/global): the footer
 * already locks in `ADDING_DWELL_MS = 300`; we duplicate the constant
 * here (rather than import) so wiring.ts has zero render-tree imports
 * and the dwell contract is a free-standing source of truth for this
 * file's tests.
 */
export const BULK_CLOSE_DWELL_MS = 300;

/**
 * Dependencies for `runBulkCommit`, the pure async core of the bulk-add
 * pipeline. Extracting these makes the orchestration directly unit-
 * testable (no `renderHook` required — the workspace lacks
 * `@testing-library/react`; see `device/discovery/components/selector/
 * table.test.tsx` §16-23 for the same workspace gap).
 *
 * Per `think-in-systems` (ai.knowledge/global): the hook (`useWorkflow`)
 * is the React-shaped adapter; `runBulkCommit` is the framework-agnostic
 * orchestrator that exercises the pure candidate-build helpers + the
 * store snapshot.
 */
export type BulkCommitDeps = Readonly<{
  /** Click-time snapshot of the multi-selection list. PR-10 closure-capture. */
  selectionIds: Discovery['id'][];
  /** Click-time snapshot of the discovery slice. */
  discoveries: readonly Discovery[];
  /** Existing cameras at submission time protect against stale discovery selections. */
  existingDevices?: readonly Device$Video[];
  /**
   * Per-modelId device-registry templates. Resolved upstream (selector.tsx
   * via `useTemplates`) and threaded through so the known-model branch of
   * `buildBulkCandidate` can fire for fingerprint-matched discoveries.
   * Defaults to an empty object when omitted — callers that don't need
   * the known-model path (or tests that don't exercise it) can leave
   * it unset and the helper falls back to the protocol branches.
   */
  templates?: T_Maybe<Record<Model['id'], T_Maybe<Template>>>;
  /**
   * Brand-detected resolutions keyed by `Brand['id']` (extended discovery).
   * Resolved upstream (selector.tsx via `useModelsByBrands` + `useTemplates`)
   * and threaded through so `buildBulkCandidate`'s brand-detected branch can
   * fire for discoveries matched to a brand fingerprint but no specific
   * model. Each entry carries the brand's generic "Other" model id + its
   * registry template. Omitted / empty → brand-only discoveries fall back to
   * the protocol defaults, exactly as before this feature.
   */
  brandDetected?: T_Maybe<
    Record<Brand['id'], T_Maybe<{ otherModelId: Model['id']; template: T_Maybe<Template> }>>
  >;
  /**
   * Registry brands, read once at click time. Names the brand discovery
   * matched on each row's `multi_add_device_added` identification props
   * (ENG-4833). Omitted → `detected_brand` is ''.
   */
  brands?: T_Maybe<Collection<Brand>>;
  /** Room id (read once at click-time from the store). */
  roomId: Room['id'];
  /** Embedded studio agent id, for browsermedia fallback (PR-3). */
  studioAgentId: T_Maybe<Agent['id']>;
  /**
   * Studio app id — used by `getTransientId` inside the ONVIF probe
   * helper so transient device IDs are scoped to the right app slot.
   * Read from the store at click-time (same closure-capture pattern as
   * `selectionIds`).
   */
  appId: AppId;
  /** Auth token (already resolved by the caller — async happens upstream). */
  token: string;
  /**
   * Current billing capacity. Caller usually passes
   * `remainingCameraSlots()`; tests inject fixed values.
   */
  slotsRemaining: number;
  /**
   * `addDevice` invocation seam — defaults to the real one in the hook;
   * tests inject mocks. Signature matches `store/utils.ts`'s `addDevice`.
   */
  addDevice: typeof addDevice;
  /**
   * `post()` notifications seam — defaults to `notifications/utils.js`;
   * tests inject jest.fn().
   */
  post: typeof post;
  /**
   * Status reporter — drives the modal's progress overlay + auth
   * prompt. The hook in `useWorkflow` plugs this into the zustand
   * store so the UI subscribes by reading `source.bulkAddStatus`.
   */
  setStatus: (status: BulkAddStatus) => void;
  /**
   * Read-only accessor to `state.external.transients` — the ONVIF
   * probe polls this slice for the bridge's capabilities response.
   * Wired by `useWorkflow` to the zustand store; tests inject a stub
   * map.
   */
  readTransients: () => Readonly<Record<string, unknown>> | undefined;
  /**
   * @deprecated Replaced by structured `emitMultiAddAnalytics`
   * emissions. Retained on the deps shape so legacy tests that pass
   * `warn: jest.fn()` keep type-checking until they migrate to the
   * `emit` seam. New code MUST NOT rely on this — the runtime no longer
   * invokes `warn()` for any skip path.
   */
  warn?: T_Maybe<(...args: unknown[]) => void>;
  /**
   * Logger seam — defaults to the global `logger`. Tests can inject a
   * stub to count `logger.error` per-leg-rejection emissions.
   */
  errorLog?: T_Maybe<(error: Error) => void>;
  /**
   * Optional override for the ONVIF probe helper — tests inject a stub
   * that resolves with a canned result without touching `addDevice` /
   * the transients slice. Defaults to the real `probeOnvifDevice`.
   */
  probeOnvif?: typeof probeOnvifDevice;
  /**
   * Optional override for the Iris-enabled link helper — same test-seam
   * pattern as `probeOnvif`. Defaults to the real `linkIrisDevice`.
   */
  linkIris?: typeof linkIrisDevice;
  /**
   * Funnel-keyed batch identifier (UUID). Threaded through every
   * `emitMultiAddAnalytics` call inside `runBulkCommit` so the PostHog
   * funnel can join `multi_add_started` → per-device events →
   * `multi_add_completed` for a single click.
   *
   * Caller (`useWorkflow`) generates this with `generateId<...>()` at
   * click time. Tests inject a fixed string for stable assertions.
   *
   * `multi_add_started` emits on first "Add N Sources" click;
   * downstream events carry the same `batchId`.
   */
  batchId: string;
  /**
   * Analytics emission seam — defaults to the real
   * `emitMultiAddAnalytics`; tests inject a jest.fn() to assert event
   * shape per `tests-require-user-story-comment` (ai.knowledge/global).
   *
   * Single point of test mocking — grep for direct PostHog `track()`
   * calls in `add/` should return no results.
   */
  emit?: T_Maybe<typeof emitMultiAddAnalytics>;
  /**
   * `device_add` seam (ENG-4950). Called once for each camera bulk add
   * commits, so bulk adds count as camera added the same way single adds
   * do. `useWorkflow` passes the same tracker single add uses. That tracker
   * sends without debouncing, so a batch is not collapsed into one event.
   * The default does nothing, for tests that don't assert analytics.
   */
  trackDeviceAdd?: T_Maybe<(properties: DeviceAddSuccessProperties) => void>;
  /**
   * Failed-device collection seam (PRD §2.11).
   *
   * Called from inside `runBulkCommit`'s per-device catch arms and the
   * post-allSettled rejection branch to record a device that never
   * committed. The renderer subscribes to the slice and displays a
   * failed-tile placeholder (gray bg + "Failed" chip + Retry/Remove).
   *
   * Production wiring (`useWorkflow`) routes this through the zustand
   * store's `producer((draft) => addFailedDevice(draft, …))`; tests
   * inject jest.fn() to assert the collection contract without
   * mounting the store.
   *
   * Optional — when omitted (legacy callers / single-add tests), the
   * collection is a no-op and the failure paths preserve their prior
   * behavior (skip + analytics emit only). Per `stay-in-scope`
   * (ai.knowledge/global) — legacy callers must remain untouched.
   *
   * The per-device failure-recording happens inside the iteration loop;
   * the post-batch notification post lives AFTER the iteration loop
   * closes, in a distinct block.
   */
  collectFailedDevice?: T_Maybe<
    (entry: {
      deviceId: string;
      name: string;
      reason:
        | 'auth'
        | 'locked'
        | 'already-linked'
        | 'commit-rejected'
        | 'bridge-disambiguation'
        | 'error';
      discovery: Discovery;
    }) => void
  >;
  /**
   * Cancellation getter (PRD §2.10).
   *
   * Called at the TOP of every per-device iteration in `runBulkCommit`.
   * When this returns `true` the loop breaks immediately: the in-flight
   * step (if any) is allowed to settle, pending devices count as
   * skipped with `skipReason: 'cancelled'`, and the pipeline emits
   * `MULTI_ADD_BATCH_CANCELLED` with the partial counts in place of
   * the usual `MULTI_ADD_COMPLETED`.
   *
   * Production wiring (`useWorkflow`) routes this through the zustand
   * store's `getState().source.cancellationRequested`; tests inject a
   * jest.fn() (often returning `false` first then `true` on the Nth
   * call to simulate the user clicking Cancel between iterations).
   *
   * Optional — when omitted (legacy callers / single-add bypass) the
   * pipeline treats it as a permanent `false` and behaves identically
   * to the pre-cancellation flow. Per `stay-in-scope`
   * (ai.knowledge/global) — legacy callers must remain untouched.
   *
   * The cancellation getter coexists alongside `postBatch` (post-loop
   * notification post) and `collectFailedDevice` (in-loop failure
   * capture); all three contribute distinct seams to the deps shape.
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  cancellationRequested?: T_Maybe<() => boolean>;
  /**
   * Auto-configured field recorder — Multi-Add Phase 2.
   *
   * Called from inside `runBulkCommit`'s post-`Promise.allSettled`
   * fulfilled branch — once per `(deviceId, fieldPath)` entry from the
   * plan's `autoConfigured` list. Records that the field's value
   * arrived from a known-good source (Device Registry template /
   * ONVIF capabilities probe / NDI discovery) rather than from a
   * default constant or user typing. The settings panel's
   * `<AutoConfiguredLabel>` subscribes to the depot slice this writes
   * into and renders a small "Auto Configured" tag beside the input;
   * the tag self-evicts when the user edits the value.
   *
   * Production wiring (`useWorkflow`) routes this through the depot's
   * `produce((draft) => addAutoConfiguredField(draft, deviceId,
   * fieldPath, value))`. Tests inject a jest.fn() to assert the call
   * shape without mounting the depot.
   *
   * The recorder fires inside the Promise.allSettled fulfilled branch.
   * Recording is keyed on `r.value`, which is `=== plan.newId` in every
   * case (the allSettled iteratee literally returns `newId`). We pass
   * `r.value` because we're already inside the fulfilled branch — that
   * branch IS the rejection guard. Recording from the rejection branch
   * would address a device that doesn't exist in state-sync.
   *
   * Optional — when omitted (legacy callers / single-add bypass / P1
   * tests that pre-date this seam) the recording is a no-op and the
   * pipeline behaves identically to before this seam. Per `stay-in-scope`
   * (ai.knowledge/global) — legacy callers must remain untouched.
   *
   * The slice itself silently drops `auth.*` paths and the B2
   * sensitive-path family (`/password|secret|token|apikey|passcode/i`);
   * the seam still skips `auth.*` upstream as a clarity guard so the
   * field-list construction below stays explicit about what it does
   * and does not record. See
   * `device/store/auto-configured-fields.slice.ts` for the slice
   * primitives.
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  recordAutoConfigured?: T_Maybe<
    (deviceId: Device['id'], fieldPath: string, value: string) => void
  >;
  /**
   * Origin registration seam.
   *
   * Called for each device whose `Promise.allSettled` leg resolved
   * `'fulfilled'` — i.e. `addDevice` succeeded and the device was
   * committed to state-sync. Failed / cancelled devices are NOT
   * registered.
   *
   * Production defaults to the real `registerMultiAddOrigin` from
   * `notifications/multi-add-origins.ts`; tests inject a jest.fn() to
   * assert the per-device registration contract without touching the
   * zustand depot.
   *
   * Optional — when omitted (legacy callers / single-add tests that
   * pre-date origin tracking) the registration is a no-op. Per
   * `stay-in-scope` (ai.knowledge/global) — legacy callers must remain
   * untouched.
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  registerOrigin?: T_Maybe<typeof registerMultiAddOrigin>;
  /**
   * Transitory-placeholder creation seam (Multi-Add Phase 2, spec §3).
   *
   * Called when a needs-credentials ONVIF camera fails its preflight
   * probe, or for every Iris-enabled camera (which always needs auth):
   * instead of pausing the pipeline on a blocking `awaiting-auth` modal,
   * `runBulkCommit` records a frontend-only placeholder so the tile +
   * "Authenticate" chip render immediately and the user authenticates
   * later from Advanced Settings (spec §0/§4). Closing the Add modal does
   * NOT lose the device — the placeholder lives in the room-level slice,
   * not modal-local state.
   *
   * Production defaults to the real `setTransitoryDevice`; tests inject a
   * jest.fn() to assert the placeholder shape WITHOUT touching the zustand
   * store (and to prove no `awaiting-auth` status is ever emitted).
   *
   * Optional — when omitted (legacy callers / single-add bypass) it
   * defaults to the real slice mutator. Per `stay-in-scope`
   * (ai.knowledge/global): the creds-preconfigured ONVIF fast-path never
   * calls this — only the needs-creds / Iris branches do.
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  createTransitory?: T_Maybe<(placeholder: TransitoryPlaceholder) => void>;
  /**
   * Persists a just-recorded pending camera on a bridge of the room, so it
   * survives a reload or an app restart and shows in every Studio client.
   * Returns the bridge it is written to, or `undefined` when no bridge can
   * hold it (the placeholder then stays in memory). Production defaults to
   * `persistPendingCamera` over the room's bridges; tests inject a jest.fn().
   */
  persistTransitory?: T_Maybe<(placeholder: TransitoryPlaceholder) => T_Maybe<Agent['id']>>;
}>;

/** Shares a pending submission and releases it on either terminal outcome. */
export const createBulkCommitRunner = () => {
  let pending: Promise<BulkCommitResult> | undefined;
  return (execute: () => Promise<BulkCommitResult>): Promise<BulkCommitResult> => {
    if (pending) return pending;
    pending = execute();
    const release = () => {
      pending = undefined;
    };
    void pending.then(release, release);
    return pending;
  };
};

/**
 * Pure async core of `handleBulkCommit`. Drives:
 *   - Pre-flight billing trim
 *   - Per-device candidate construction via `buildBulkCandidate`
 *   - agentId fallback chain
 *   - Promise.allSettled fan-out (30-parallel)
 *   - Outcome metadata (`successCount`, `newDeviceIds`)
 *   - Toast on zero-success
 *
 * Does NOT touch the dialog (modal close + focus return are caller-
 * owned; see `useWorkflow` for the React-side wiring). Returns the
 * result so the caller can drive close timing + focus return on its
 * own clock.
 *
 */
export const runBulkCommit = async (deps: BulkCommitDeps): Promise<BulkCommitResult> => {
  const {
    selectionIds: inputSelectionIds,
    discoveries,
    templates,
    brandDetected,
    brands,
    roomId,
    studioAgentId,
    appId,
    token,
    slotsRemaining,
    addDevice: addDeviceFn,
    post: postFn,
    setStatus,
    readTransients,
    errorLog,
    probeOnvif: probeOnvifOverride,
    // `linkIris` is no longer consumed by bulk-add (spec §0/§4) — Iris
    // auth moved to Advanced Settings. Field kept on the deps shape for
    // back-compat; intentionally not destructured here.
    batchId,
    emit: emitOverride,
    trackDeviceAdd,
    // Failed-device collection seam (PRD §2.11). No-op when the caller
    // doesn't wire it (legacy tests / single-add bypass).
    collectFailedDevice,
    // Cancellation getter (PRD §2.10). Treated as permanent-false when
    // omitted; production wiring routes through the zustand store;
    // tests inject a jest.fn() driver.
    cancellationRequested,
    // Auto-configured field recorder (Multi-Add Phase 2). No-op when
    // the caller doesn't wire it (P1 tests / single-add bypass / legacy
    // multi-add callers that pre-date the seam). Wired in
    // `useWorkflow.handleBulkCommit` to the depot's
    // `addAutoConfiguredField` mutator.
    recordAutoConfigured,
    // Origin registration seam. Defaults to the real
    // `registerMultiAddOrigin`; tests inject jest.fn() to assert
    // per-device registration without touching the depot.
    // Per `stay-in-scope` (ai.knowledge/global): only the fulfilled
    // branch below may call this — wizard / single-add MUST NOT.
    registerOrigin: registerOriginOverride,
    // Transitory-placeholder creation seam (spec §3). See resolution to
    // `createTransitoryFn` below.
    createTransitory,
    // Pending-camera persistence seam. See `persistTransitoryFn` below.
    persistTransitory
  } = deps;
  // Snapshot before any await. A caller can mutate the array during async
  // preflight (template fetch); the count and the loop must share this copy.
  const selectionIds = [...new Set(inputSelectionIds)];
  // Local helper: returns true ONLY when the caller wired the getter
  // AND the getter currently returns true. Avoids a tri-state at every
  // loop iteration. Per `think-in-systems` (ai.knowledge/global) — the
  // primitive is the getter; this is a thin readability shim.
  const isCancelled = (): boolean => cancellationRequested?.() === true;
  // Own the lookup before awaiting late templates: callers pass an Immer-frozen store snapshot.
  const templatesById: Record<Model['id'], T_Maybe<Template>> = { ...templates };
  // Brand-detected resolutions keyed by brandId (extended discovery).
  // Empty-object fallback keeps the per-discovery lookup indexable for
  // callers that never populated the mirror (single-add / legacy tests).
  const brandDetectedById: Record<
    Brand['id'],
    T_Maybe<{ otherModelId: Model['id']; template: T_Maybe<Template> }>
  > = brandDetected ?? {};
  // Probe seam — production wiring + tests use the same handle. The
  // Iris LINK is no longer driven from bulk-add (spec §0/§4): an
  // Iris-enabled camera now records a placeholder and authenticates
  // later via Advanced Settings, so `linkIris` is intentionally not
  // resolved here (the dep stays on the shape for back-compat).
  const probeFn = probeOnvifOverride ?? probeOnvifDevice;
  // ENG-4833: identification props for a row's `multi_add_device_added`. Rows
  // are always identified by discovery; the driver follows the model the row
  // is committed with. Placeholders have no candidate yet and report the
  // discovery's own model with control assumed (no operator chose No Control).
  const identificationFor = (
    discovery: Discovery,
    candidate?: Readonly<{ modelId?: T_Maybe<Model['id']>; control?: unknown }>
  ): ReturnType<typeof resolveBulkAddIdentification> =>
    resolveBulkAddIdentification({
      discovery,
      savedModelId: candidate ? candidate.modelId : discovery.modelId,
      hasControl: candidate ? Boolean(candidate.control) : true,
      templates: templatesById,
      brandDetected: brandDetectedById,
      brands
    });
  // Transitory-placeholder creation seam (spec §3). Defaults to the real
  // slice mutator; tests inject a jest.fn(). Per `reference-rules-in-comments`.
  const createTransitoryFn = createTransitory ?? setTransitoryDevice;
  // Pending-camera persistence seam: production writes to a bridge of the room.
  const persistTransitoryFn =
    persistTransitory ??
    ((placeholder: TransitoryPlaceholder) =>
      persistPendingCamera(placeholder, {
        candidates: pendingCameraHostCandidates(roomId),
        token
      }));
  // Analytics emit seam — production wires through the canonical helper;
  // tests inject jest.fn(). Single point of test mocking.
  const emit = emitOverride ?? emitMultiAddAnalytics;
  // Origin registration seam. Production defaults to
  // `registerMultiAddOrigin`; tests inject jest.fn().
  // Per `stay-in-scope` (ai.knowledge/global): wizard path never
  // touches the multi-add origin slice — the boundary is load-bearing
  // to keep the wizard regression from re-appearing. Only the fulfilled
  // leg below calls this.
  const registerOriginFn = registerOriginOverride ?? registerMultiAddOrigin;

  // Zero `console.warn` calls remain in `runBulkCommit`. The legacy
  // `warn` seam stays on the deps shape (see @deprecated note on
  // BulkCommitDeps.warn) for backwards-compat with legacy tests, but
  // the runtime no longer invokes it — every skip path emits a
  // structured `multi_add_device_needs_attention` instead.
  const onError = errorLog ?? ((e: Error) => logger.error(e));

  // Failed-device collection shim (PRD §2.11). Adjacent to the
  // analytics emit calls in every failure branch below; same call-site
  // contract (deviceId + name + reason + discovery). No-op when the
  // caller didn't wire the seam.
  // Per `think-in-systems` (ai.knowledge/global): the slice is the
  // primitive; this thin local helper just adapts the call shape so the
  // failure branches stay readable.
  const recordFailed = (
    discovery: Discovery,
    deviceId: string,
    reason:
      | 'auth'
      | 'locked'
      | 'already-linked'
      | 'commit-rejected'
      | 'bridge-disambiguation'
      | 'error'
  ): void => {
    if (!collectFailedDevice) return;
    collectFailedDevice({
      deviceId,
      name: discovery.name,
      reason,
      discovery
    });
  };

  // Closure-capture: freeze the user's click-time intent count here.
  // The caller may pass a live array reference; we read .length exactly
  // once so post-await mutations (sibling code path tweaking the same
  // array) cannot rewrite the toast denominator or the attemptedCount
  // return value.
  const attemptedCount = selectionIds.length;

  // `multi_add_started` emits on first "Add N Sources" click. The
  // caller (`useWorkflow.handleBulkCommit`) already guards the entry
  // against bulkMode + N≥2; runBulkCommit's `attemptedCount > 0` guard
  // mirrors that without re-checking the store. The wall-clock anchor
  // lives in `runBulkCommit` (not the caller) so test stubs producing
  // synthetic deps still surface `time_to_completion_ms` in the
  // completion event.
  const batchStartTime = performance.now();
  emit(EVENT_NAMES.MULTI_ADD_STARTED, {
    batchId,
    selectedDeviceCount: attemptedCount,
    studioId: roomId
  });

  // ---- Pre-flight: billing re-check ----
  // Trim to fit live capacity. Warn (not error) — partial commit is
  // correct behavior; the chips/toast surface the rest of the story.
  // Each trimmed device emits a structured `slot-trimmed` event so the
  // funnel can attribute batch shrinkage to billing rather than user
  // error.
  let activeIds: Discovery['id'][] = selectionIds;
  // Billing-trimmed devices are a capacity skip, not a commit failure: they never
  // reach the commit loop and each emits its own slot-trimmed needs-attention event.
  // Track the count so it can be excluded from failedCount in the summary below.
  let trimmedCount = 0;
  if (slotsRemaining < attemptedCount) {
    activeIds = selectionIds.slice(0, Math.max(0, slotsRemaining));
    const trimmedIds = selectionIds.slice(Math.max(0, slotsRemaining));
    trimmedCount = trimmedIds.length;
    for (const trimmedId of trimmedIds) {
      const d = discoveries.find((x) => x.id === trimmedId);
      emit(EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION, {
        batchId,
        deviceId: trimmedId.toString(),
        deviceName: d?.name ?? '(unknown)',
        skipReason: 'slot-trimmed',
        studioId: roomId
      });
    }
  }

  // ---- Build per-device commit plans (sequential pipeline) ----
  //
  // Previously this loop was a pure synchronous filter (build
  // candidate, resolve agentId, push plan), and the fan-out below did
  // ALL adds in parallel. The pipeline is now sequential because ONVIF
  // discoveries need a transient probe (`probeOnvifDevice`) before we
  // can commit them with a real RTSP URI, and that probe may pause to
  // collect credentials from the user. Going sequential keeps the
  // modal's progress overlay unambiguous ("Configuring {name}
  // ({i}/{N})") and prevents racy transient lifecycles on the bridge
  // — see studio.client PR #1100 commit 5247bae9 for the pre-monorepo
  // iteration of this same constraint ("already exists" issues from
  // parallel transients).
  //
  // Non-ONVIF discoveries (NDI, RTSP family, browsermedia, ONVIF rows
  // that already carry credentials) skip the probe entirely and just
  // build a candidate from `buildBulkCandidate` — same as before.

  setStatus({ kind: 'preparing' });

  type Plan = Readonly<{
    discovery: Discovery;
    candidate: NonNullable<ReturnType<typeof buildBulkCandidate>>;
    agentId: Agent['id'];
    newId: Device['id'];
    /**
     * Per-plan auto-configured field list — Multi-Add Phase 2.
     *
     * Populated at plan-build time, consumed inside the post-allSettled
     * fulfilled branch (the rejection guard). Captures fields whose
     * values arrived from a known source (Device Registry template /
     * ONVIF probe / NDI discovery), so the settings panel's
     * `<AutoConfiguredLabel>` can render beside each one.
     *
     * Defaults applied via `defaultsDeep` (VISCA / RTSP / HTTP / ONVIF
     * fallbacks) are NOT recorded — those are constants, not source-
     * derived auto-fills.
     *
     * Empty array when no source supplied any field (generic RTSP
     * fallback, etc.). Optional on the type so a future caller that
     * builds the Plan by other means can omit it; the recording loop
     * below treats `undefined` and `[]` identically.
     *
     * Per `reference-rules-in-comments` (ai.knowledge/global).
     */
    autoConfigured?: ReadonlyArray<{ fieldPath: string; value: string }>;
  }>;

  // Thin local helper: iterates the plan's autoConfigured list and
  // forwards each entry to the wired-up recorder. The caller invokes
  // this only from inside the Promise.allSettled fulfilled branch
  // (the rejection guard), so the forwarded device id always refers
  // to a device that successfully landed in state-sync. On rejection
  // the helper is never called, so nothing lands in the slice for a
  // failed commit.
  //
  // `auth.*` paths are skipped upstream as a clarity guard — the slice
  // would silently drop them anyway (UX rationale: user-typed creds
  // must not lie). Keeping the skip here too makes the field-list
  // construction below an honest source-of-truth and saves a no-op
  // depot round trip.
  //
  // No-op when the caller didn't wire `recordAutoConfigured` (legacy
  // callers / single-add bypass / P1 tests). Per `think-in-systems`
  // (ai.knowledge/global) — the slice mutator is the primitive; this
  // is a thin adapter shim.
  //
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const recordAuto = (
    deviceId: Device['id'],
    autoConfigured: ReadonlyArray<{ fieldPath: string; value: string }> | undefined
  ): void => {
    if (!recordAutoConfigured) return;
    if (!autoConfigured || autoConfigured.length === 0) return;
    for (const { fieldPath, value } of autoConfigured) {
      // Defensive `auth.*` skip — the slice's `isUserInputFieldPath`
      // guard drops these silently, but skipping here keeps the
      // recording-site explicit about its contract.
      if (/^auth\./i.test(fieldPath)) continue;
      recordAutoConfigured(deviceId, fieldPath, value);
    }
  };

  const plans: Plan[] = [];
  // Sources the user explicitly skipped via the auth prompt (or the
  // retry probe failed). Reported in the final toast as "Y skipped".
  let skippedCount = 0;
  // Hoisted out of the post-allSettled block so the Iris-enabled
  // branch can bump them directly. Iris-enabled discoveries are
  // committed as agent-links (no `addDevice` call) — they bypass the
  // `plans` array entirely, so we account for their success here in
  // the loop body.
  const newDeviceIds: Device['id'][] = [];
  let successCount = 0;
  // Transitory placeholders recorded via `recordTransitory` (needs-auth
  // Iris/ONVIF cameras — spec §3). These DO land a canvas tile (so they
  // bump `successCount` / `addedDeviceCount`), but they are NOT genuinely
  // connected: the user still authenticates later from Advanced Settings.
  // Tracking them separately lets the `multi_add_completed` summary express
  // the engineer's breakdown — "N added, of which X placeholder and Y
  // failed" — instead of hardcoding `needsAttentionCount` to 0.
  // `fullyConfiguredCount` is then `successCount - placeholderCount`.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  let placeholderCount = 0;
  // Finalized devices: `addDevice` commits that landed a real Studio device.
  // Counted positively (not as success − placeholder) so a future kind of
  // success must be classified before it can reach `added_device_count`.
  let finalizedCount = 0;
  // Count of Iris-enabled discoveries that completed `linkAgent`
  // successfully — used by the caller to fire a renderer reload after
  // modal close. Multi-Add Phase 2 (spec §0/§4) moved Iris linking OUT
  // of bulk-add: an Iris camera now records a placeholder and links
  // later from Advanced Settings, so bulk-add never links and this stays
  // 0. Kept on `BulkCommitResult` for back-compat; the caller's
  // `irisLinkedCount > 0` reload simply never triggers from bulk-add.
  // Per `stay-in-scope` (ai.knowledge/global): the single-add reload
  // contract (`credentials-dialog.tsx`) is untouched.
  const irisLinkedCount = 0;
  const discoveryById = new Map<Discovery['id'], Discovery>(discoveries.map((d) => [d.id, d]));

  const totalToProcess = activeIds.length;
  let processedIndex = 0;

  // Record a transitory placeholder for a needs-credentials camera
  // (spec §3) instead of pausing on a blocking auth modal. The
  // placeholder lives in the room-level slice, so closing the Add modal
  // does NOT lose the device (spec §0 — CEO review). Counts as a
  // success: the tile appears on the canvas immediately and the user
  // authenticates later from Advanced Settings. The synthetic device's
  // RTSP video aspect (`buildPlaceholderVideo`) is what makes
  // `isVideoDevice` true so Advanced Settings can open (spec §4).
  //
  // Per `reference-rules-in-comments` (ai.knowledge/global): implements
  // spec §3 (canvas render via slice) + §3.3 (Authenticate chip).
  //
  // Returns false when the discovery has no resolvable host — the caller
  // then falls back to its failed-slice skip (the placeholder's RTSP
  // default needs a host; a hostless placeholder could never render or
  // authenticate, spec §11).
  const recordTransitory = (discovery: Discovery, kind: TransitoryKind): boolean => {
    const placeholder: TransitoryPlaceholder | null = buildPlaceholderForDiscovery(
      discovery,
      kind,
      roomId
    );
    if (!placeholder) {
      return false;
    }
    // Persist on a bridge so the camera survives a restart and shows in every
    // Studio client; without an eligible bridge it stays in memory.
    const hostAgentId = persistTransitoryFn(placeholder);
    createTransitoryFn(hostAgentId ? { ...placeholder, hostAgentId } : placeholder);
    traceCameraAdd('bulk.placeholder-created', {
      batchId,
      placeholderId: placeholder.id,
      transitoryKind: kind,
      hostAgentId: hostAgentId ?? null,
      discovery: describeAddDiscovery(discovery)
    });
    // ENG-4486 Fix A.1: register the transitory placeholder as a multi-add
    // origin (under this batch) so the post-add observer fires the
    // "Authenticate" toast for an auth-required camera — which otherwise
    // never became an origin (origins were only registered on the fulfilled
    // `addDevice` branch below, and an auth-required camera commits LATER via
    // `dispatchAuthSubmit` with a new id). On authentication the origin is
    // migrated placeholder→real (`migrate-transitory-origin.ts`). This runs
    // ONLY inside `runBulkCommit` (the bulk/transitory path) — wizard /
    // single-add never call `recordTransitory`, so the boundary that keeps
    // the wizard regression away is preserved (`stay-in-scope`).
    // Per `reference-rules-in-comments` (ai.knowledge/global).
    registerOriginFn([placeholder.id.toString()], batchId);
    // Account it as added — the device now has a canvas tile + chip.
    // `locateProxy` is a no-op for a placeholder id, so threading it
    // through `newDeviceIds` (focus-return target) is safe.
    newDeviceIds.push(placeholder.id);
    successCount++;
    // A placeholder lands a tile but is not fully configured — count it
    // both as added (successCount, above) AND as a placeholder so the
    // summary can subtract it out of `fullyConfiguredCount`.
    placeholderCount++;
    emit(EVENT_NAMES.MULTI_ADD_DEVICE_ADDED, {
      batchId,
      // The placeholder's device id (the tile, the multi-add origin and the
      // later configuration-completed event all use it), not the discovery
      // row's id, which is a host:port or a deviceNN key (ENG-4950).
      deviceId: placeholder.id.toString(),
      deviceName: discovery.name,
      studioId: roomId,
      ...identificationFor(discovery)
    });
    return true;
  };

  // PRD §2.10 — track whether the loop terminated via cancellation so
  // the post-loop emit path can choose between the normal
  // `MULTI_ADD_COMPLETED` and the `MULTI_ADD_BATCH_CANCELLED` event. We
  // also need this to surface a separate `cancelled` toast wording:
  // "X added, Y skipped (cancelled)".
  let cancelled = false;

  // A fingerprint match can arrive before its registry template. Commit must
  // not substitute the generic model while that template is still loading.
  const unresolvedModels = new Set<Model['id']>();
  const missingModelIds = new Set<Model['id']>();
  for (const id of activeIds) {
    const discovery = discoveryById.get(id);
    if (
      discovery &&
      'modelId' in discovery &&
      discovery.modelId &&
      !templatesById[discovery.modelId]
    ) {
      missingModelIds.add(discovery.modelId);
    }
  }
  // Registry requests share a mutex. Await each selected model so cancellation
  // can stop before queuing another request; the main loop settles cancelled rows.
  for (const modelId of missingModelIds) {
    if (isCancelled()) break;
    try {
      traceCameraAdd('fingerprint.template-request', { batchId, modelId });
      templatesById[modelId] = await getTemplate(modelId, token);
      traceCameraAdd('fingerprint.template-resolved', {
        batchId,
        modelId,
        template: describeDriverTemplate(templatesById[modelId])
      });
    } catch (error) {
      traceCameraAdd('fingerprint.template-failed', {
        batchId,
        modelId,
        failure: describeAddFailure(error)
      });
      unresolvedModels.add(modelId);
      onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  for (const did of activeIds) {
    // PRD §2.10 — cancellation guard at the TOP of each iteration.
    // When the user has clicked Cancel on the bulk-progress overlay,
    // break out before starting work on the next device. Already-
    // committed devices remain in the room (no rollback — `addDevice`
    // / `linkAgent` are not reversible).
    //
    // Failure-branch handling (auth-skip, retry-fail, build-candidate-
    // refuse, no-agentId) lives below in this loop; `postBatchFn(...)`
    // fires once OUTSIDE the loop. Pending devices (the not-yet-
    // attempted tail of `activeIds`) are bookkept as 'cancelled' skips
    // below the loop so the analytics funnel + the failed-tile
    // renderer have an honest accounting.
    //
    // Per `reference-rules-in-comments` (ai.knowledge/global).
    if (isCancelled()) {
      cancelled = true;
      break;
    }
    processedIndex++;
    const discovery = discoveryById.get(did);
    if (!discovery) {
      // Defensive: selection id with no live discovery row (the
      // selector prunes dropped rows, but the snapshot is at click time).
      // Structured emit with skip_reason 'discovery-stale' so the funnel
      // can spot snapshot-vs-live drift without spelunking console logs.
      emit(EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION, {
        batchId,
        deviceId: did.toString(),
        deviceName: '(stale)',
        skipReason: 'discovery-stale',
        studioId: roomId
      });
      skippedCount++;
      continue;
    }

    setStatus({
      kind: 'configuring',
      name: discovery.name,
      index: processedIndex,
      total: totalToProcess
    });

    // Record routing before either protocol can short-circuit the real bridge add.
    const selectedTrace = describeAddDiscovery(discovery);
    const selectedHost = selectedTrace.controlHost ?? selectedTrace.videoHost;
    traceCameraAdd('bulk.route', {
      batchId,
      selectedId: did,
      roomId,
      route: isIrisDiscovery(discovery)
        ? 'iris-placeholder'
        : isOnvifDiscovery(discovery)
          ? 'onvif-probe'
          : 'direct-add',
      discovery: selectedTrace,
      matchingIdCount: discoveries.filter((item) => item.id === did).length,
      sameHostDiscoveries:
        selectedHost == null
          ? []
          : discoveries
              .map(describeAddDiscovery)
              .filter((item) => (item.controlHost ?? item.videoHost) === selectedHost)
    });

    // ---- Iris-enabled camera → transitory placeholder (spec §3/§4) ----
    //
    // An Iris-enabled camera advertises its own embedded agent over
    // mDNS; "adding" one is an agent-link operation that always needs
    // credentials (the camera's device-login API rejects any
    // unauthenticated probe — there is no anonymous first attempt).
    //
    // BEFORE (multi-add-phase1): bulk-add drove a blocking
    // `awaiting-auth` modal per camera and called `linkIrisDevice`
    // inline. That made bulk-add block (10 cameras = 10 modals) and
    // closing the modal lost the device (spec §0).
    //
    // NOW (spec §0/§4, engineer decision §15.2 — "Iris se muestra en el
    // canvas"): record a frontend-only placeholder and move on. The tile
    // + "Authenticate" chip render immediately; the user authenticates
    // later from Advanced Settings, where the W4 dispatcher replays
    // `linkIrisDevice` by `transitoryKind`. NO `setStatus(awaiting-auth)`
    // — bulk-add is now non-blocking.
    //
    // Host/port derivation lives in `buildPlaceholderForDiscovery`: the
    // bulk-add selector packs Iris-enabled discoveries with a composite
    // `address = "${host}:${port}"` and the mDNS port at `video.port`.
    //
    // Per `reference-rules-in-comments` (ai.knowledge/global).
    if (isIrisDiscovery(discovery)) {
      if (!recordTransitory(discovery, 'iris')) {
        // No resolvable host — fall back to the failed-slice skip so the
        // device still surfaces a Retry path (spec §11).
        recordFailed(discovery, did.toString(), 'error');
        skippedCount++;
      }
      // Iris-enabled rows never hit the candidate-build / agentId-
      // resolve / plan-push tail of this loop iteration — the placeholder
      // IS the bulk-add outcome; auth is deferred to Advanced Settings.
      continue;
    }

    // ONVIF can provide its stream URI now; otherwise credentials and setup remain editable later.
    let probedVideo: ReturnType<typeof buildBulkCandidate> extends (infer R) | undefined
      ? R extends { video?: unknown }
        ? R['video']
        : undefined
      : undefined = undefined;

    let resolvedCredentials: { username: string; password: string } | undefined;

    if (isOnvifDiscovery(discovery)) {
      const probeDeps = {
        roomId,
        agentId: (discovery.agentId ?? studioAgentId ?? '') as Agent['id'],
        appId,
        token,
        transients: { read: readTransients },
        traceBridgeDevice: (id: string) =>
          describeBridgeDevice(
            broker.acquire((discovery.agentId ?? studioAgentId ?? '') as Agent['id'])?.state,
            id
          )
      };

      // First attempt: use credentials the discovery carried (often
      // undefined — most ONVIF advertisements don't ship creds).
      const initialCreds =
        'control' in discovery && discovery.control
          ? ((discovery.control as { credentials?: unknown }).credentials as
              | { username: string; password: string }
              | undefined)
          : undefined;
      // Seed the resolved creds with whatever the discovery had — if
      // the first probe succeeds (rare path: discovery already had
      // valid creds), this is what we commit with.
      resolvedCredentials = initialCreds;
      const result = await probeFn(discovery, initialCreds as never, probeDeps);
      traceCameraAdd('bulk.probe-result', {
        batchId,
        discoveryId: discovery.id,
        kind: result.kind,
        reason: result.kind === 'failed' ? result.reason : undefined
      });

      // A probe enriches configuration; failure must not replace the selected device with a placeholder.
      if (result.kind === 'success') {
        probedVideo = result.videoConfig as never;
      }
    }

    // ---- Build the commit candidate ----
    //
    // `buildBulkCandidate` contract: undefined candidate means the
    // helper rejected the discovery (e.g. browsermedia with empty
    // path; unrecognized protocol). Skip + log.
    //
    // Bug 6 fix: thread the per-modelId template + the resolution host
    // through so `buildBulkCandidate`'s known-model branch can fire for
    // fingerprint-matched discoveries.
    const template = discovery.modelId ? templatesById[discovery.modelId] : undefined;
    if (discovery.modelId && !template && unresolvedModels.has(discovery.modelId)) {
      recordFailed(discovery, did.toString(), 'error');
      skippedCount++;
      continue;
    }
    const videoHost =
      'video' in discovery && discovery.video && 'host' in discovery.video
        ? (discovery.video as { host?: string }).host
        : undefined;
    const host = discovery.address ?? videoHost;
    // Brand-detected input (extended discovery): only for a VIDEO-aspect
    // discovery matched to a brand fingerprint but no specific model.
    // Threads the resolved generic "Other" model id + template into
    // `buildBulkCandidate`'s brand-detected branch. Known-model
    // discoveries (they have `modelId`) take the known-model path above
    // and pass `undefined` here, so this cannot steal them.
    const brandResolution =
      'video' in discovery && !discovery.modelId && discovery.brandId
        ? brandDetectedById[discovery.brandId]
        : undefined;
    const brandDetectedInput = brandResolution
      ? { modelId: brandResolution.otherModelId, template: brandResolution.template }
      : undefined;
    let candidate = buildBulkCandidate(discovery, template, host, brandDetectedInput);
    traceCameraAdd('fingerprint.candidate-built', {
      batchId,
      discovery: describeAddDiscovery(discovery),
      cachedTemplate: describeDriverTemplate(
        discovery.modelId ? templatesById[discovery.modelId] : undefined
      ),
      selectedTemplate: describeDriverTemplate(template),
      brandDetectedTemplate: describeDriverTemplate(brandDetectedInput?.template),
      brandDetectedModelId: brandDetectedInput?.modelId,
      candidate: candidate ? describeAddDiscovery(candidate) : null
    });
    if (!candidate) {
      // buildBulkCandidate's reject path lands under
      // `skip_reason: error` (no specific subreason — the helper does
      // not surface a discriminated cause). Bump skippedCount so the
      // completion summary reflects this skip.
      emit(EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION, {
        batchId,
        deviceId: did.toString(),
        deviceName: discovery.name,
        skipReason: 'error',
        studioId: roomId
      });
      // buildBulkCandidate refused (PRD §2.11); the device never
      // reaches state-sync so it must live in the failed slice for the
      // placeholder tile to render. Adjacent to the analytics emit.
      recordFailed(discovery, did.toString(), 'error');
      skippedCount++;
      continue;
    }

    // Discover the stream without changing the selected control protocol or endpoint.
    if (isOnvifDiscovery(discovery) && probedVideo) {
      candidate = {
        ...candidate,
        video: {
          ...(probedVideo as Record<string, unknown>),
          ...(resolvedCredentials ? { credentials: resolvedCredentials } : {})
        } as NonNullable<typeof candidate>['video']
      };
    }

    // ---- Build the per-plan auto-configured field list ----
    //
    // Walks the THREE auto-fill sources (known-model template, ONVIF
    // probe, NDI) and accumulates one entry per source-derived field. Defaults
    // applied via `defaultsDeep` (VISCA / RTSP / HTTP / ONVIF
    // fallbacks) are intentionally NOT recorded — those are constants,
    // not source-derived auto-fills, and labelling them would mislead
    // the producer about whose value they're looking at.
    //
    // Source discrimination is structural (which branch of
    // `buildBulkCandidate` fired):
    //
    //   1. Known-model template match — when `template` resolved AND
    //      `discovery.modelId` is set AND discovery has a video aspect
    //      AND not iris-enabled. Same predicate as
    //      `buildBulkCandidate`'s known-model entry (utils.ts:242-247).
    //      Records brand + model + video.* (when `template.video`
    //      supplied them) + control.* (when `template.control`
    //      supplied them).
    //
    //   2. ONVIF probe success — when `isOnvifDiscovery(discovery)`
    //      AND `probedVideo` was returned by `probeFn`. Records
    //      video.* from the probed URI. VISCA control.* are defaults
    //      applied by the ONVIF post-probe rewrite block above —
    //      explicitly NOT recorded (they would lie).
    //
    //   3. NDI discovery — when `discovery.video.protocol === NDI`.
    //      Records `video.protocol` (the only NDI field that arrives
    //      from the source itself; NDI host/name come from the mDNS
    //      payload as transports, not as auto-configured fields).
    //
    // Iris-enabled rows are explicitly out of scope — they never reach
    // this point in the loop (the Iris branch above `continue`s).
    //
    // `auth.*` paths are NEVER added to this list — the slice would
    // silently drop them anyway (the recorded fields are user-typed,
    // not source-derived), and the seam's `recordAuto` helper also
    // double-checks. See `device/store/auto-configured-fields.slice.ts`.
    //
    // Per `think-in-systems` (ai.knowledge/global): the list is a
    // narrow, plain data structure consumed by `recordAuto` after the
    // commit settles. No new abstraction.
    //
    // Per `reference-rules-in-comments` (ai.knowledge/global).
    const autoConfigured: { fieldPath: string; value: string }[] = [];
    // Source 1: Known-model template match.
    // The entry guards (template + video aspect + modelId + !irisEnabled)
    // mirror `buildBulkCandidate`'s known-model entry condition
    // (utils.ts:242-248). But entry alone is not sufficient: when
    // `buildKnownModelCandidate` returns undefined (e.g. RTSP branch
    // with no `template.video`, utils.ts:91), `buildBulkCandidate` FALLS
    // THROUGH to the protocol branches and applies `ViscaIpDefaults` for
    // control — which is defaults, not source-derived. Re-call the helper
    // with the exact args `buildBulkCandidate` used (utils.ts:249-251)
    // to verify the known-model path actually fired before recording
    // any brand/model/video/control entries from this candidate.
    //
    // Per `verify-and-escalate` (ai.knowledge/global): the predicate now
    // matches the EXIT condition, not just the ENTRY condition.
    const knownVerify = (() => {
      if (
        !template ||
        !('video' in discovery) ||
        !discovery.video ||
        !discovery.modelId ||
        discovery.irisEnabled
      ) {
        return undefined;
      }
      const videoHost = (discovery.video as { host?: string }).host;
      const knownHost = discovery.address ?? videoHost;
      return buildKnownModelCandidate(discovery, template, undefined, knownHost);
    })();
    const isKnownModelMatch = !!knownVerify;

    if (isKnownModelMatch) {
      // Brand + model are ALWAYS source-derived when the known-model
      // branch fired: brand id from the template's model record, model
      // id from the discovery's fingerprint.
      const brandId = (template as Template).model?.brandId;
      if (brandId !== undefined && brandId !== null) {
        autoConfigured.push({
          fieldPath: 'manufacturer.brand',
          value: String(brandId)
        });
      }
      const modelId = (discovery as { modelId?: unknown }).modelId;
      if (modelId !== undefined && modelId !== null) {
        autoConfigured.push({
          fieldPath: 'manufacturer.model',
          value: String(modelId)
        });
      }

      // video.* — record only when the template supplied a video block.
      // For NDI known-model the template's video is usually undefined
      // (NDI video is selection-derived); the NDI-source branch below
      // still records `video.protocol` for those.
      const templateVideo = (template as Template).video;
      if (templateVideo && candidate.video) {
        const vid = candidate.video as Record<string, unknown>;
        if (vid.protocol !== undefined) {
          autoConfigured.push({
            fieldPath: 'video.protocol',
            value: String(vid.protocol)
          });
        }
        if (vid.host !== undefined) {
          autoConfigured.push({
            fieldPath: 'video.host',
            value: String(vid.host)
          });
        }
        if (vid.port !== undefined) {
          autoConfigured.push({
            fieldPath: 'video.port',
            value: String(vid.port)
          });
        }
        if (vid.path !== undefined) {
          autoConfigured.push({
            fieldPath: 'video.path',
            value: String(vid.path)
          });
        }
      }

      // control.* — record only when the template supplied a control
      // block. For the known-model branch the template's control is
      // either fully defined (HTTP / VISCA-IP with brand specifics) or
      // undefined (NDI without template control). Defaults filled in
      // by `buildBulkCandidate`'s post-known-model fallback (VISCA
      // defaults) are NOT recorded.
      const templateControl = (template as Template).control;
      if (templateControl && candidate.control) {
        const ctl = candidate.control as Record<string, unknown>;
        if (ctl.protocol !== undefined) {
          autoConfigured.push({
            fieldPath: 'control.protocol',
            value: String(ctl.protocol)
          });
        }
        if (ctl.host !== undefined) {
          autoConfigured.push({
            fieldPath: 'control.host',
            value: String(ctl.host)
          });
        }
        if (ctl.port !== undefined) {
          autoConfigured.push({
            fieldPath: 'control.port',
            value: String(ctl.port)
          });
        }
      }
    }

    // Source: Brand-detected (extended-discovery brand fingerprint).
    // Verify the brand-detected branch actually fired in buildBulkCandidate
    // (not a fall-through to protocol defaults) before recording, using the
    // same args buildBulkCandidate used. Mutually exclusive with the
    // known-model source (that path requires discovery.modelId).
    const brandVerify = (() => {
      if (
        !brandDetectedInput ||
        !('video' in discovery) ||
        !discovery.video ||
        discovery.modelId ||
        discovery.irisEnabled ||
        !discovery.brandId ||
        !brandDetectedInput.modelId ||
        !brandDetectedInput.template
      ) {
        return undefined;
      }
      const brandVideoHost = (discovery.video as { host?: string }).host;
      const brandHost = discovery.address ?? brandVideoHost;
      return buildBrandDetectedCandidate(
        discovery,
        brandDetectedInput.modelId,
        brandDetectedInput.template,
        brandHost
      );
    })();
    // The `&& brandDetectedInput?.template` re-assertion narrows
    // `brandDetectedInput` (and `.template`) back to defined for the
    // type-checker inside the block — `brandVerify` being truthy already
    // implies both, but TS cannot infer that link across the IIFE.
    if (brandVerify && brandDetectedInput && brandDetectedInput.template) {
      // brand + model are source-derived: brand from the fingerprint, model
      // is the resolved generic "Other" model id.
      if (discovery.brandId !== undefined && discovery.brandId !== null) {
        autoConfigured.push({ fieldPath: 'manufacturer.brand', value: String(discovery.brandId) });
      }
      if (brandDetectedInput.modelId !== undefined && brandDetectedInput.modelId !== null) {
        autoConfigured.push({
          fieldPath: 'manufacturer.model',
          value: String(brandDetectedInput.modelId)
        });
      }
      // video.* from the registry generic template (source-derived).
      const templateVideo = brandDetectedInput.template.video;
      if (templateVideo && candidate.video) {
        const vid = candidate.video as Record<string, unknown>;
        if (vid.protocol !== undefined)
          autoConfigured.push({ fieldPath: 'video.protocol', value: String(vid.protocol) });
        if (vid.host !== undefined)
          autoConfigured.push({ fieldPath: 'video.host', value: String(vid.host) });
        if (vid.port !== undefined)
          autoConfigured.push({ fieldPath: 'video.port', value: String(vid.port) });
        if (vid.path !== undefined)
          autoConfigured.push({ fieldPath: 'video.path', value: String(vid.path) });
      }
      // control.* recorded ONLY when the registry template supplied control.
      // Generic templates clear control (ptzDriver blanked), so the VISCA
      // default filled in by buildBrandDetectedCandidate is NOT source-derived
      // and must NOT be recorded (matches the known-model rule for defaults).
      const templateControl = brandDetectedInput.template.control;
      if (templateControl && candidate.control) {
        const ctl = candidate.control as Record<string, unknown>;
        if (ctl.protocol !== undefined)
          autoConfigured.push({ fieldPath: 'control.protocol', value: String(ctl.protocol) });
        if (ctl.host !== undefined)
          autoConfigured.push({ fieldPath: 'control.host', value: String(ctl.host) });
        if (ctl.port !== undefined)
          autoConfigured.push({ fieldPath: 'control.port', value: String(ctl.port) });
      }
    }

    // Source 2: ONVIF probe success.
    // `probedVideo` holds the URI returned by the bridge from ONVIF
    // capabilities — that IS the source-of-truth for video.*. We do
    // NOT record auth.* (the slice would drop them; auth.* is user-input,
    // not source-derived). We do NOT record VISCA control.* (those are defaults
    // applied by the ONVIF post-probe rewrite block above; they are
    // constants, not source-derived).
    if (isOnvifDiscovery(discovery) && probedVideo) {
      const pv = probedVideo as Record<string, unknown>;
      if (pv.protocol !== undefined) {
        autoConfigured.push({
          fieldPath: 'video.protocol',
          value: String(pv.protocol)
        });
      }
      if (pv.host !== undefined) {
        autoConfigured.push({
          fieldPath: 'video.host',
          value: String(pv.host)
        });
      }
      if (pv.port !== undefined) {
        autoConfigured.push({
          fieldPath: 'video.port',
          value: String(pv.port)
        });
      }
      if (pv.path !== undefined) {
        autoConfigured.push({
          fieldPath: 'video.path',
          value: String(pv.path)
        });
      }
    }

    // Source 3: NDI discovery.
    // Records `video.protocol` only — NDI host/name are transport
    // facts from the mDNS payload, not auto-configured in the labelled-
    // field sense (the producer can't edit "NDI source name" the way
    // they edit a Stream URL). The known-model branch above may also
    // have recorded video.protocol when an NDI discovery matched a
    // template; both writes are idempotent on the slice (same value)
    // so dedup is not necessary.
    if (
      'video' in discovery &&
      !!discovery.video &&
      'protocol' in discovery.video &&
      discovery.video.protocol === VideoProtocol.NDI
    ) {
      // Only push when the known-model branch did NOT already record
      // it — keeps the list compact for the most common case (single
      // entry, single dispatch).
      const alreadyRecordedProtocol = autoConfigured.some(
        (entry) => entry.fieldPath === 'video.protocol'
      );
      if (!alreadyRecordedProtocol) {
        autoConfigured.push({
          fieldPath: 'video.protocol',
          value: String(discovery.video.protocol)
        });
      }
    }

    // Source 4: Iris-enabled link — INTENTIONALLY out of scope. Iris
    // rows `continue` above before reaching this point; the link
    // contract owns the entire device record via state-sync. No labels.

    // agentId fallback chain.
    const isBrowsermedia =
      'video' in discovery &&
      !!discovery.video &&
      'protocol' in discovery.video &&
      discovery.video.protocol === VideoProtocol.Local;

    const agentId =
      discovery.agentId ?? discovery.agentIds?.[0] ?? (isBrowsermedia ? studioAgentId : undefined);

    if (!agentId) {
      // `bridge-disambiguation` is the closest skip_reason match for
      // "no agentId resolved". The PRD §10 bridge-multi-agent open
      // question is the user-facing context for this branch. The deep-
      // link mapping routes this reason to the Configure bridge section.
      emit(EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION, {
        batchId,
        deviceId: did.toString(),
        deviceName: discovery.name,
        skipReason: 'bridge-disambiguation',
        studioId: roomId
      });
      // No agentId resolved (PRD §2.11); never committed to state-sync.
      // Surface in the failed slice so the user has a Retry/Remove
      // path. Adjacent to the analytics emit.
      recordFailed(discovery, did.toString(), 'bridge-disambiguation');
      skippedCount++;
      continue;
    }

    // A stale discovery selection must not create a second source on the same
    // bridge. Uses the table's policy, so another transport of a camera already
    // in the room (same IP, same bridge) is skipped too.
    if (
      deps.existingDevices?.some(
        (device) => device.agentId === agentId && isDiscoveryInUse(device, discovery)
      )
    ) {
      traceCameraAdd('bulk.already-present', { batchId, discoveryId: discovery.id, agentId });
      skippedCount++;
      continue;
    }

    plans.push({
      discovery,
      candidate,
      agentId,
      newId: generateId<Device['id']>(),
      // `recordAuto` consumes this inside the post-allSettled fulfilled
      // branch (the rejection guard). If addDevice rejects this list
      // is never forwarded, so the slice never points at a non-existent
      // device.
      autoConfigured
    });
  }

  // ---- Fan out: Promise.allSettled (30-parallel) ----
  //
  // Each leg builds the full `Minimist`-shaped device descriptor
  // (mirrors single-add `handleCommit` line 103-107) and calls
  // `addDevice`, which internally calls `setPendingDevice` so all 30
  // parallel calls produce 30 distinct pending entries. Failures are
  // captured by allSettled — one rejection cannot short-circuit the
  // batch.
  setStatus({ kind: 'committing', total: plans.length });
  const results = await Promise.allSettled(
    plans.map(async ({ discovery, candidate, agentId, newId }) => {
      // Bug 6.5 fix: propagate `modelId` + the template-derived `name`
      // for fingerprint-matched discoveries. Single-add does this in
      // source/panel.tsx (~line 104-105) by writing
      // `draft.candidate.modelId = selection.modelId` and
      // `draft.candidate.name = template.model?.name ?? selection.name`
      // when a model is known. That sync is gated to skip in bulk mode
      // (panel.tsx line 117: `if (bulkMode === true) return;`) so the
      // wiring path is the only place those fields can land for
      // bulk-add. Without them the resulting tile shows the raw wire
      // name ("192.168.0.23") and brand "Other — brand is not listed"
      // even though the video URX itself was built from the template.
      const template = discovery.modelId ? templatesById[discovery.modelId] : undefined;
      const resolvedName = template?.model?.name ?? discovery.name;
      // Only attach modelId when the known-model branch actually fired
      // (template resolved). Unmatched discoveries — generic RTSP,
      // ONVIF without registry entry, browsermedia — stay model-less,
      // mirroring the pre-fingerprint legacy behavior.
      const modelOverride =
        template && 'modelId' in discovery && discovery.modelId
          ? { modelId: discovery.modelId }
          : {};
      const deviceData = {
        ...defaults({}, candidate, { name: resolvedName }),
        ...modelOverride,
        agentId,
        id: newId
      };

      traceCameraAdd('bulk.commit-start', {
        configuration: describeAddDiscovery(deviceData),
        template: describeDriverTemplate(template),
        batchId,
        discoveryId: discovery.id,
        deviceId: newId,
        agentId
      });
      const saved = await addDeviceFn(roomId, agentId, deviceData, token);
      traceCameraAdd('bulk.commit-complete', {
        batchId,
        discoveryId: discovery.id,
        deviceId: newId,
        agentId
      });
      // ENG-4950: send device_add with the same properties single add sends,
      // and like single add only for a confirmed save (a superseded add, such
      // as a tile deleted mid-add, resolves without saving). Inside try so a
      // telemetry failure can never turn a committed camera into a rejected leg.
      try {
        if (saved?.kind === 'saved') {
          trackDeviceAdd?.({
            ...discoveryDeviceAddProperties(
              deviceData,
              roomId,
              template?.model,
              Boolean(discovery.modelId)
            ),
            // Joins this device_add to its multi_add_* batch events.
            batchId
          });
        }
      } catch {
        /* Telemetry is not persistence. */
      }
      return newId;
    })
  );

  // ---- Compute outcome metadata ----
  //
  // `successCount` + `newDeviceIds` were declared before the per-
  // discovery loop so the Iris-enabled branch could increment them
  // directly. Here we layer in the results from the Promise.allSettled
  // fan-out (ONVIF / RTSP / NDI / browsermedia legs that produced a
  // commit plan).
  for (let i = 0; i < results.length; i++) {
    const r = results[i]!;
    const plan = plans[i]!;
    if (r.status === 'fulfilled') {
      successCount++;
      finalizedCount++;
      newDeviceIds.push(r.value);
      // Register the committed device as a multi-add origin so the
      // observer hook can later fire a "needs configuration"
      // notification for this device. Registration happens ONLY here —
      // on a fulfilled `addDevice` result — so failed / cancelled
      // devices are NOT registered.
      //
      // Per `stay-in-scope` (ai.knowledge/global): wizard path never
      // touches the multi-add origin slice — the boundary is
      // load-bearing to keep the wizard regression from re-appearing.
      // The wizard calls `addDevice` directly (outside this function);
      // it never reaches this `runBulkCommit` code path.
      registerOriginFn([r.value.toString()], batchId);
      // Emit per-device success for the allSettled-resolved leg.
      // Iris-enabled rows are handled in their dedicated branch above
      // and never reach this loop, so this emit is exclusively for
      // ONVIF / RTSP / NDI / browsermedia. ONVIF rows that hit the
      // inline-auth prompt already emitted `success_after_prompt`
      // above; the post-commit emit here is additive (carries the
      // resolved deviceId).
      emit(EVENT_NAMES.MULTI_ADD_DEVICE_ADDED, {
        batchId,
        deviceId: r.value.toString(),
        deviceName: plan.discovery.name,
        studioId: roomId,
        ...identificationFor(plan.discovery, plan.candidate)
      });
      // Auto-configured field recording (Multi-Add Phase 2).
      // We pass `r.value` (which equals `plan.newId` in every case —
      // the allSettled iteratee literally returns `newId`) because
      // we're already inside the fulfilled branch; THAT branch is the
      // rejection guard. The else branch below never invokes the
      // recorder, so no entries land for a failed commit.
      recordAuto(r.value, plan.autoConfigured);
    } else {
      traceCameraAdd('bulk.commit-failed', {
        batchId,
        discoveryId: plan.discovery.id,
        deviceId: plan.newId,
        failure: describeAddFailure(r.reason)
      });
      // Structured analytics emit alongside the logger.error. The
      // error stays in Sentry via logger.error; the analytics emit
      // drives the PostHog funnel's `failed_count`.
      // Per `use-canonical-logger-not-console`
      // (ai.knowledge/platform/studio.client) — both channels stay.
      onError(<Error>r.reason);
      emit(EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION, {
        batchId,
        deviceId: plan.newId.toString(),
        deviceName: plan.discovery.name,
        skipReason: 'commit-rejected',
        studioId: roomId
      });
      // Promise.allSettled rejection means addDevice failed (bridge /
      // network / etc.) per PRD §2.11; device never committed to
      // state-sync. Collect for the failed-tile placeholder. Together
      // with the in-loop failure branches above, this guarantees the
      // all-fail-batch acceptance: when successCount === 0 &&
      // attemptedCount > 0, every attempted device that didn't commit
      // lands in the slice. Adjacent to the analytics emit.
      recordFailed(plan.discovery, plan.newId.toString(), 'commit-rejected');
    }
  }

  // ---- PRD §2.10 — cancellation accounting ----
  //
  // When the loop broke via `cancellationRequested`, the not-yet-
  // attempted tail of `activeIds` needs to be bookkept as `cancelled`
  // skips so the analytics funnel can honestly attribute batch
  // shrinkage to user cancellation (vs auth/locked/etc.). We emit a
  // per-device `MULTI_ADD_DEVICE_NEEDS_ATTENTION` with the canonical
  // `skipReason: 'cancelled'` for each pending device — the enum value
  // is reserved in `analytics/types/properties.ts` precisely for this
  // branch.
  //
  // Pending devices are NOT routed through `recordFailed` — they never
  // attempted a commit, so the failed-tile renderer (which surfaces
  // commit-failures with Retry/Remove) is not the right surface. The
  // user can re-launch the bulk-add modal and re-select them; the
  // multiSelection slice persists across the modal-close-reopen cycle
  // until the user starts a new batch.
  //
  // Pending devices count as skipped with skip_reason: cancelled.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  if (cancelled) {
    // `processedIndex` is the count of devices the loop fully iterated.
    // The device the loop broke ON sits at activeIds[processedIndex];
    // every entry from there to the end is pending.
    const pendingIds = activeIds.slice(processedIndex);
    for (const pendingId of pendingIds) {
      const d = discoveryById.get(pendingId);
      emit(EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION, {
        batchId,
        deviceId: pendingId.toString(),
        deviceName: d?.name ?? '(unknown)',
        skipReason: 'cancelled',
        studioId: roomId
      });
      skippedCount++;
    }
  }

  // ---- Toast outcome surfacing ----
  //
  // The toast surfaces the user's outcome in one of four shapes:
  //   - all succeeded → no toast
  //   - zero success → error toast "0 of N sources added"
  //   - partial success with skipped sources → info toast
  //     "X of N sources added. Y skipped." (auth-required ONVIF that
  //     the user skipped via the prompt land here)
  //   - cancellation → warning toast "X added, Y skipped (cancelled)"
  //     (PRD §2.10). The cancel toast intentionally differs from the
  //     "X of N" partial-success copy because the denominator misleads
  //     in a cancelled batch: the user stopped the pipeline themselves;
  //     reporting "X of N" implies N-X failed, when in fact N-X were
  //     never attempted. The wording is verbatim:
  //     "X added, Y skipped (cancelled)".
  //
  // Use `post()` (verified primitive — `notifications/utils.js`), NOT
  // `useToast` (which does not exist in this repo).
  setStatus({ kind: 'done' });

  if (cancelled) {
    // Toast wording verbatim. Severity is 'warning' (not 'error') —
    // cancellation is a user-driven outcome, not a system failure;
    // the bell badge already routes severity in postBatchFn below for
    // the persistent notification.
    postFn({
      id: `multi-add-cancelled-${Date.now()}`,
      title: `${successCount} added, ${skippedCount} skipped (cancelled)`,
      message: 'Batch cancelled by user',
      severity: 'warning'
    });
  } else if (successCount === 0 && attemptedCount > 0) {
    postFn({
      id: `multi-add-zero-success-${Date.now()}`,
      title: `0 of ${attemptedCount} sources added`,
      message: 'Try again or add manually',
      severity: 'error'
    });
  } else if (skippedCount > 0 && successCount > 0) {
    postFn({
      id: `multi-add-partial-skip-${Date.now()}`,
      title: `${successCount} of ${attemptedCount} sources added`,
      message: `${skippedCount} skipped`,
      severity: 'warning'
    });
  }

  // `multi_add_completed` emits with full outcome counts + duration.
  // `multi_add_partial_success` co-emits when ≥1 device is non-fully-
  // configured (i.e. skippedCount > 0 OR addedCount < attemptedCount).
  //
  // The summary expresses the engineer's breakdown — "N devices added, of
  // which X placeholder and Y failed":
  //   addedDeviceCount     (N) = successCount (tiles landed, incl. placeholders)
  //   placeholderCount     (X) = transitory needs-auth tiles (recordTransitory)
  //   failedCount          (Y) = attempted − added − skipped − trimmed
  //     (billing-trimmed devices already emitted `slot-trimmed` and never
  //      entered the commit loop, so they are a capacity skip, not a failure)
  //   fullyConfiguredCount     = added − placeholder (genuinely connected)
  //   needsAttentionCount      = placeholderCount (these need user auth)
  //
  // Canonical split (canonical-event-map.json), on every event above:
  //   added_device_count        = finalized devices only (real Studio devices)
  //   transitional_device_count = transitional placeholders, reported apart
  // so added_device_count + transitional_device_count == addedDeviceCount.
  // Per rule: preserve-historical-telemetry-during-renames (ai.knowledge/platform)
  // — addedDeviceCount keeps its shipped meaning; the finalized-only count is a
  // new key, never an alias of it.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const timeToCompletionMs = Math.round(performance.now() - batchStartTime);
  const addedDeviceCount = successCount;
  const failedCount = attemptedCount - successCount - skippedCount - trimmedCount;
  // Genuinely-connected = added tiles minus transitory placeholders. Clamp
  // at 0 defensively (placeholderCount is always ≤ successCount by
  // construction — every placeholder bumps successCount in the same step).
  const fullyConfiguredCount = Math.max(0, addedDeviceCount - placeholderCount);
  // PRD §2.10 — cancellation replaces the normal completion emit with
  // `MULTI_ADD_BATCH_CANCELLED`. The funnel
  // queries for "batch cancellation rate" key off this event
  // explicitly; folding a `cancelled` flag onto `multi_add_completed`
  // would muddy the funnel step counts (every cancellation would also
  // appear as a "completed" even though no commit ran on the tail).
  // `MULTI_ADD_COMPLETED` is NOT emitted on cancellation (replaced by
  // `MULTI_ADD_BATCH_CANCELLED`). `MULTI_ADD_PARTIAL_SUCCESS` is also
  // suppressed on the cancelled branch — partial success has an
  // implicit "completed organically" semantic that does not apply
  // when the user stopped the pipeline themselves.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  // One bag for all three summary events so their counts cannot drift apart.
  const batchSummary: MultiAddEmitProps = {
    batchId,
    selectedDeviceCount: attemptedCount,
    addedDeviceCount,
    fullyConfiguredCount,
    placeholderCount,
    needsAttentionCount: placeholderCount,
    failedCount: Math.max(0, failedCount),
    timeToCompletionMs,
    studioId: roomId,
    added_device_count: finalizedCount,
    transitional_device_count: placeholderCount
  };
  if (cancelled) {
    emit(EVENT_NAMES.MULTI_ADD_BATCH_CANCELLED, { ...batchSummary });
  } else {
    emit(EVENT_NAMES.MULTI_ADD_COMPLETED, { ...batchSummary });
    // Partial success = anything other than 100% added without skip.
    // Includes "0 of N" (all-fail batch) and "X of N, Y skipped".
    if (addedDeviceCount < attemptedCount && attemptedCount > 0) {
      emit(EVENT_NAMES.MULTI_ADD_PARTIAL_SUCCESS, { ...batchSummary });
    }
  }

  // ---- Rejected-commit surface (bug 3, spec §3.3 / V3.2) ----
  //
  // The masthead bell-batch notification that used to post here (via
  // `postBatchFn`, into the `multiAddBatchNotifications` slice) was removed
  // with the pinned tray card. Per the V3.2 synthesis override the
  // rejected-commit outcome must still surface via `post()` — and it does:
  // the `post()` toast block ABOVE (cancelled / zero-success / partial-
  // skip) is now the single surface for a rejected or skipped bulk commit.
  // Committed devices that then land in a needs-attention chip bucket are
  // surfaced separately by the observer hook's `post()`
  // (use-multi-add-bucket-notifications.ts); hard-failed devices get a
  // failed-tile chip. So there is no bell batch to synthesize here, and
  // dropping it loses no surface.
  //
  // Per `reference-rules-in-comments` / `stay-in-scope` (ai.knowledge/global).

  return {
    successCount,
    attemptedCount,
    skippedCount,
    newDeviceIds,
    irisLinkedCount
  };
};

/**
 * Schedule modal close + focus return.
 *
 * Extracted so the timing concern (MAX(allSettled, 300ms)) and the
 * tile-vs-trigger focus-return contract can be unit-tested in isolation
 * from `runBulkCommit`. The hook composes both.
 *
 * @param result - Outcome from `runBulkCommit`.
 * @param closeFn - Modal-close callback (typically `χref(apiRef)?.close(true)`).
 * @param focusFn - Focus-target lookup callback. Called with the first
 *   new device id when `successCount > 0`; should return the element to
 *   focus (or null). Defaults to a `document.querySelector` against the
 *   STag tile selector.
 */
export const scheduleBulkClose = (
  result: BulkCommitResult,
  closeFn: () => void,
  focusFn?: T_Maybe<(deviceId: Device['id']) => HTMLElement | null>
): void => {
  // Close FIRST, then refocus on the next tick. Radix Dialog returns
  // focus to the trigger element by default; we override that to the
  // first new tile when we have one. The setTimeout(0) lets Radix's
  // own focus-return microtask flush before ours runs.
  closeFn();

  if (result.successCount > 0 && result.newDeviceIds.length > 0) {
    const targetId = result.newDeviceIds[0]!;
    const lookup =
      focusFn ??
      ((id: Device['id']): HTMLElement | null =>
        document.querySelector<HTMLElement>(
          buildSTagSelector(Sel.MainRoomLeftTrayVideo, 'devices', id.toString())
        ));
    setTimeout(() => {
      const target = lookup(targetId);
      if (target) {
        target.focus();
      }
    }, 0);
  }
  // successCount === 0: rely on Radix's default trigger-element focus
  // return — no explicit work needed.
};

/** @internal */
export const useWorkflow = (apiRef: RefObject<Api | null>): Wiring => {
  const producer = useProducer();
  const rawStore = ensureNotNil(useContext(StoreContext));
  const active = useRef(true);
  const closed = useRef(false);
  const generation = useRef(0);
  // True only while `invalidate` writes the store, so the raw-store watcher does not re-enter it.
  const invalidating = useRef(false);
  const reportExit = useRef<((category: 'cancelled' | 'superseded') => void) | undefined>(
    undefined
  );
  const prompt = usePrompt();
  // The probe helper polls `state.external.transients` to detect when
  // the bridge has resolved
  // the camera's RTSP URI from ONVIF capabilities. We hold a reference
  // to the zustand store itself so the probe can `getState()` for fresh
  // reads inside its 500ms poll loop (a memoized selector won't
  // re-evaluate inside an async loop).
  const storeApi = useContext(StoreContext);

  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();
  const deviceLifecycleTracker = createDeviceLifecycleTracker(track);

  const panel = useStore((state) => state.panel);

  const invalidate = useLatestFn((category: 'cancelled' | 'superseded' = 'superseded') => {
    reportExit.current?.(category);
    active.current = false;
    generation.current++;
    invalidating.current = true;
    producer((draft) => {
      const attempt = draft.finalSave;
      if (!attempt || attempt.status === 'saved') return;
      // Per settle-every-terminal-branch (ai.knowledge/global): resolve the branch by value.
      if (attempt.rawPending) {
        // Raw IO is still in flight; work().finally resolves the branch once it actually settles.
        attempt.status = 'superseded';
      } else if (attempt.stage === 'pre-mutation') {
        // Settled and never sent: clear it so a corrected candidate can be added fresh (B1),
        // rather than dead-ending the dialog with an uncertain "could not confirm" message.
        // The store shallow-merges producer output, so a `delete` leaves the key in place;
        // assigning undefined is what actually clears it (cast satisfies exactOptionalPropertyTypes).
        (draft as { finalSave?: FinalSave | undefined }).finalSave = undefined;
        draft.previewPaused = false;
      } else {
        // Settled after the mutation was sent: identity drift makes persistence unknowable.
        attempt.status = 'uncertain';
      }
    });
    invalidating.current = false;
  });
  // Multi-Add Phase 2 analytics primitive for the hook-side emits
  // (single-add baseline + add-source modal open/cancel). `runBulkCommit`
  // has its own injectable `emit` seam; the hook calls the helper directly.
  const emit = emitMultiAddAnalytics;
  // True once a commit (single or bulk) actually landed a device, so the
  // modal-close visibility handler can distinguish a real cancel (no
  // commit) from a commit-driven close. Per `reference-rules-in-comments`
  // (ai.knowledge/global).
  const committedRef = useRef(false);

  // When the user attempts to close the modal mid-bulk-selection with
  // N>5 items, prompt to confirm discarding the batch. ≤5 closes
  // silently (re-selecting is cheap).
  // Per `reference-rules-in-comments` (ai.knowledge/global) —
  // cancel-confirm contract.
  const { tryDiscard: tryBulkDiscard } = useBulkDiscardConfirmation();

  // Bulk-add reads `source` (multiSelection + discoveries) and the existing
  // single-add slice in the same selector — `shallow` keeps the subscription
  // cost flat. `multiSelection` is captured by closure at click time
  // (snapshot below), NOT via `useStore.getState()`.
  const store = useStore(
    // 'source' (per ENG-3960 §1.7) lets the device_add (step 8) emit classify the
    // embedded flow from the in-flight discovery, matching the wizard step emits.
    (µ) =>
      pick(µ, [
        // appId — `getTransientId(appId)` in the ONVIF probe needs it
        // to scope transient device IDs.
        'appId',
        'roomId',
        'agentId',
        'manufacturer',
        'candidate',
        'onboarding',
        'panel',
        'previews',
        'source'
      ]),
    shallow
  );

  // Studio (embedded) agent for browsermedia fallback. When a
  // discovery has no agentId AND its video is browsermedia, fall back
  // to the first embedded agent so the local source can still commit.
  // For non-browsermedia rows with no agentId, the pipeline skips with
  // `console.warn`.
  const studioAgentId = useAgents<Agent['id'] | undefined>((agents) => {
    return agents.find((a) => a.embedded)?.id;
  });

  // A→B→A must not revive A's completion. Subscribe to the owner, not rendered snapshots.
  useEffect(() => {
    const unsubscribe = rawStore.subscribe((state) => {
      const attempt = state.finalSave;
      // `invalidate` writes the store (clear/supersede/uncertain), which re-enters this listener;
      // the `invalidating` guard stops it recursing back into `invalidate`.
      if (!attempt || attempt.status === 'superseded' || invalidating.current) return;
      const { snapshot } = attempt;
      if (
        state.flowId !== snapshot.flowId ||
        state.roomId !== snapshot.roomId ||
        state.agentId !== snapshot.agentId ||
        // Panel remounts can replace a candidate with an equal value; preserve its attempt.
        !isEqual(state.candidate, snapshot.candidate) ||
        state.source.selection?.id !== snapshot.selection?.id ||
        state.source.selection?.agentId !== snapshot.selection?.agentId
      ) {
        invalidate();
      }
    });
    return () => {
      unsubscribe();
      invalidate();
    };
  }, [rawStore, invalidate]);

  const cleanupPreviews = async (previews: State['previews'], token: string): Promise<void> => {
    // Await every removal before releasing ownership, including when a sibling rejects.
    const outcomes = await Promise.allSettled(
      Object.entries(previews).map(async ([aspect, preview]) => {
        await removePreview(preview, token);
        producer((draft) => {
          const key = aspect as keyof State['previews'];
          if (draft.previews[key]?.deviceId === preview?.deviceId) delete draft.previews[key];
        });
      })
    );
    const failure = outcomes.find((outcome) => outcome.status === 'rejected');
    // The terminal owner extracts only allowlisted status; never forward this raw cause to events.
    if (failure?.status === 'rejected') throw failure.reason;
  };

  const handleCleanup = useLatestFn(() => {
    if (closed.current) return;
    closed.current = true;
    const state = rawStore.getState();
    invalidate('cancelled');
    producer((draft) => {
      draft.previewPaused = true;
    });
    // A pending commit already owns these removals; closing must not start them again.
    if (!state.finalSave?.rawPending) {
      // Per B2: bound the cleanup so a hung removal cannot chain the next dialog's setup.
      void settlePreviewSetup(
        getAccessTokenSilently,
        (token) => cleanupPreviews(state.previews, token),
        PREVIEW_CLEANUP_DEADLINE_MS
      ).catch(() => {});
    }
    producer((draft) => {
      draft.source.selection = undefined;
      // The next add starts unidentified, with the not-listed / D1 dialogs closed.
      if (draft.identification) resetIdentification(draft);
    });
  });

  const handleClose = useLatestFn(() => {
    handleCleanup();
    χref(apiRef)?.close(true);
  });

  const handleCancel = useLatestFn(() => {
    const owner = generation.current;
    prompt.confirm({
      message:
        'Are you sure you want to exit now? By choosing to exit before saving you will lose all information about this video source.',
      okay: {
        label: 'Yes, Exit and Cancel',
        ...STagNew(Sel.Dialogs, 'confirm-cancel', 'add-video-source')
      },
      onConfirm: (answer: boolean) => {
        // An old confirmation must not close a reopened or retargeted dialog.
        if (answer && owner === generation.current && !closed.current) {
          handleClose();
        }
      },
      title: 'Cancel add source'
    });
  });

  const handleCommit = useLatestFn(
    async (options?: CommitOptions): Promise<Device['id'] | undefined> => {
      // The onboarding footer seeds control settings and commits in the same tick.
      const state = rawStore.getState();
      if (closed.current) return undefined;
      const previous = state.finalSave;
      if (previous && (previous.rawPending || previous.status !== 'retryable')) return undefined;
      active.current = true;
      const snapshot = previous?.snapshot ?? {
        ...pick(state, [
          'candidate',
          'manufacturer',
          'agentId',
          'roomId',
          'flowId',
          'addMethod',
          'onboarding'
        ]),
        selection: state.source.selection,
        // How the camera was identified travels with the commit so a retry reports the same answer.
        identification: state.identification,
        // Keep the commit-only control out of candidate identity: previews must never prepare it.
        commitOptions: options
      };
      const { agentId, manufacturer, roomId, onboarding } = snapshot;
      const candidate =
        snapshot.commitOptions && 'control' in snapshot.commitOptions
          ? { ...snapshot.candidate, control: snapshot.commitOptions.control }
          : snapshot.candidate;
      const singleAddProtocol = candidate?.video?.protocol?.toString();
      const emitSingleAdd = (
        event: typeof EVENT_NAMES.SINGLE_ADD_STARTED | typeof EVENT_NAMES.SINGLE_ADD_COMPLETED,
        outcome?: 'success' | 'error'
      ): void => {
        try {
          emit(event, {
            ...(singleAddProtocol ? { protocol: singleAddProtocol } : {}),
            ...(outcome ? { singleAddOutcome: outcome } : {}),
            studioId: roomId?.toString()
          });
        } catch {
          /* Optional analytics cannot strand the raw save owner. */
        }
      };
      emitSingleAdd(EVENT_NAMES.SINGLE_ADD_STARTED);
      const id = previous?.deviceId ?? generateId<Device['id']>();
      let stage: FinalSave['stage'] = 'pre-mutation';
      let operation: AddFlowOperation = 'auth-access-token';
      const diagnostic = createAddFlowDiagnostic({
        actor: 'selected-bridge',
        agentId,
        roomId,
        sourceId: snapshot.selection?.id?.toString()
      });
      reportExit.current = (category) =>
        diagnostic.report({ operation, category, retry: 'none', deviceId: id });
      let presented = false;
      const current = (): boolean =>
        active.current &&
        rawStore.getState().finalSave?.deviceId === id &&
        rawStore.getState().finalSave?.status !== 'superseded';
      const update = (fn: (attempt: FinalSave) => void): void =>
        producer((draft) => {
          if (draft.finalSave?.deviceId === id) fn(draft.finalSave);
        });
      producer((draft) => {
        draft.previewPaused = true;
        draft.finalSave = { deviceId: id, snapshot, stage, status: 'pending', rawPending: true };
      });
      const catalog = (() => {
        try {
          return resolveDeviceAnalyticsCatalogContextFromModel(
            manufacturer.config?.model,
            candidate.modelId
          );
        } catch {
          return {};
        } // Missing optional catalog data cannot prevent the save from settling.
      })();
      // Preserve the model-selection cohort independently of the source-entry method.
      const addModeProps = candidate.modelId
        ? { add_mode: 'one_click' as const, model_id_source: 'backend' as const }
        : manufacturer.config?.model
          ? { add_mode: 'manual' as const, model_id_source: 'manual_picker' as const }
          : {};
      const cohort = {
        ...catalog,
        ...addModeProps,
        device_id: id,
        studio_id: roomId.toString(),
        attemptReference: diagnostic.reference,
        ...(candidate.video?.protocol ? { protocol: candidate.video.protocol.toString() } : {}),
        ...(candidate.video?.protocol ? { deviceType: candidate.video.protocol.toString() } : {}),
        ...resolveEmbeddedFlowProps(isEmbeddedDiscovery(snapshot.selection)),
        ...(snapshot.addMethod ? { [ONBOARDING_PROPERTIES.ADD_METHOD]: snapshot.addMethod } : {})
      };
      const fail = (uncertain: boolean, timeout = false, cause?: unknown): void => {
        if (!current() || presented) return;
        presented = true;
        // Only the owned media-token wrapper narrows a pre-mutation failure further.
        if (
          operation === 'device-add-prepare' &&
          classifyPreviewPrepareCause(cause, 'preview-prepare', diagnostic.reference).operation ===
            'media-token'
        ) {
          operation = 'media-token';
        }
        const status = addFlowStatusFromCause(cause);
        const category: AddFlowOutcome['category'] = timeout
          ? 'timeout'
          : status === 401 || status === 403
            ? 'refused'
            : status && status >= 500
              ? 'service'
              : 'unknown';
        diagnostic.report({
          operation,
          category,
          status,
          retry: uncertain ? 'check-state' : 'retry',
          deviceId: id
        });
        update((attempt) => {
          attempt.status = uncertain ? 'uncertain' : 'retryable';
        });
        // Per B9: a retryable settle must let previews recover so the operator who goes Back to
        // re-check settings sees a live preview (and a Retry affordance), not a stuck Offline pane.
        if (!uncertain && !closed.current) {
          producer((draft) => {
            draft.previewPaused = false;
            draft.previewGeneration += 1;
          });
        }
        // Never forward arbitrary IO errors, credentials, or URLs into custom events or exceptions.
        try {
          deviceLifecycleTracker.trackDeviceAddFailed({
            ...cohort,
            operation,
            ...(status !== undefined ? { status } : {}),
            error_type: timeout ? 'timeout' : 'unknown',
            error_message: uncertain
              ? 'Device save could not be confirmed.'
              : 'Device add failed before mutation.'
          });
        } catch {
          /* Analytics cannot hold the footer busy. */
        }
      };
      let timer: ReturnType<typeof setTimeout>;
      const deadline = new Promise<undefined>((resolve) => {
        timer = setTimeout(() => {
          // Per B3 / settle-every-terminal-branch: a deadline before the mutation was sent leaves
          // nothing on the wire, so it is retryable; only a post-mutation deadline is uncertain.
          fail(stage !== 'pre-mutation', true);
          resolve(undefined);
        }, 30_000);
      });
      const work = async (): Promise<Device['id'] | undefined> => {
        try {
          const token = await getAccessTokenSilently();
          if (!token) throw new Error('missing-token');
          operation = 'preview-remove';
          // Per B2: bound the cleanup Save waits on. A removal that never settles must not make
          // Save "uncertain" or wedge later previews — proceed to the permanent add and leave the
          // stale transient for a later best-effort pass. A real removal FAILURE (not a timeout)
          // still keeps the add retryable. knowledge: addDevice-single-flight-pattern.
          const cleanup = await settlePreviewSetup(
            async () => token,
            (cleanupToken) => cleanupPreviews(rawStore.getState().previews, cleanupToken),
            PREVIEW_CLEANUP_DEADLINE_MS
          );
          if (cleanup.timedOut) {
            const stalled = rawStore.getState().previews;
            for (const key of Object.keys(stalled) as (keyof State['previews'])[]) {
              const preview = stalled[key];
              if (preview) abandonPreview(preview);
            }
            // Repo logger, never console.* (knowledge: use-canonical-logger-not-console).
            logger.info('device add preview cleanup exceeded its budget; proceeding with save', {
              operation: 'preview-remove',
              category: 'timeout',
              deviceId: id,
              attemptReference: diagnostic.reference
            });
          } else if (cleanup.error !== undefined) {
            throw cleanup.error;
          }
          if (!current() || presented) return undefined;
          const deviceData = {
            ...defaults({}, candidate, {
              name: resolveModelLessDeviceName(candidate, manufacturer.defaultName)
            }),
            // ENG-4832: typed brand/model + identified_by persist on the StudioDevice record.
            ...toPersistedIdentification(snapshot.identification),
            agentId,
            id
          };
          operation = 'device-add-prepare';
          const result = await addDevice(roomId, agentId, deviceData, token, undefined, {
            onStage: (next) => {
              stage = next;
              if (next === 'mutation-sent') operation = 'device-mutation';
              if (next === 'save-pending' || next === 'saved') operation = 'device-save';
              update((attempt) => {
                attempt.stage = next;
              });
            },
            shouldContinue: () => current() && !presented
          });
          if (!current() || presented) return undefined;
          if (result?.kind !== 'saved' || result.deviceId !== id) {
            fail(true);
            return undefined;
          }
          presented = true;
          diagnostic.report({
            operation: 'device-save',
            category: 'success',
            retry: 'none',
            deviceId: id
          });
          clearTimeout(timer);
          update((attempt) => {
            attempt.status = 'saved';
          });

          committedRef.current = true;

          // ENG-4941: tally what the customer typed for an unlisted camera. Fire-and-forget:
          // `recordTypedCamera` never rejects and is deliberately not awaited, so a failed
          // write can neither fail nor delay a save that already succeeded.
          if (deviceData.typedBrand || deviceData.typedModel) {
            void recordTypedCamera({
              token,
              organizationId: depot.getState().roomRecords?.[roomId]?.organizationId,
              deviceId: id,
              typedBrand: deviceData.typedBrand,
              typedModel: deviceData.typedModel
            });
          }
          try {
            const selection = snapshot.selection;
            const template = manufacturer?.config;
            const entries: ReadonlyArray<{
              fieldPath: string;
              value: string;
            }> = [
              // Known-model HTTP path — only emits when selection,
              // template, and substituted candidate are all present.
              // Empty array otherwise.
              ...(selection?.modelId && template
                ? computeKnownModelAutoConfigured(selection, template, {
                    video: candidate?.video,
                    control: candidate?.control
                  })
                : []),
              // ONVIF capabilities path — only emits when the selection
              // is an ONVIF-control discovery (mutually exclusive with
              // the known-model branch above; both empty for generic
              // RTSP / NDI fallbacks).
              ...(selection && isOnvifDiscovery(selection)
                ? computeOnvifCapabilitiesAutoConfigured(candidate?.video)
                : [])
            ];
            if (entries.length > 0) {
              depot.getState().produce((draft) => {
                for (const { fieldPath, value } of entries) {
                  addAutoConfiguredField(draft, id, fieldPath, value);
                }
              });
            }
          } catch (error) {
            // Recording must NEVER break the commit flow. The device is
            // already added; the label is a UX informational. Log via
            // canonical logger so Sentry catches anomalies, then move
            // on.
            logger.error(<Error>error);
          }

          // A saved device stays saved even if optional analytics or notification delivery fails.
          try {
            const depotState: DepotState = depot.getState();
            const room = depotState.rooms?.[roomId];
            const existingVideoIds = room?.videoIds ?? [];
            const uniqueExistingVideoIds = new Set(filterTransients(existingVideoIds));
            const existingDevices = uniqueExistingVideoIds.size;
            // Is `1` here, due to the camera already being added to state beofre this check
            const wasFirstCamera = existingDevices >= 1;

            deviceLifecycleTracker.trackDeviceAddSuccess({
              ...cohort,
              operation: 'device-save',
              deviceType: deviceData.video?.protocol?.toString() ?? '',
              brand_name: catalog.brand_name ?? '',
              model_name: catalog.model_name ?? '',
              device_brand_id: catalog.device_brand_id ?? '',
              device_model_id: catalog.device_model_id ?? '',
              ...resolveVideoStreamProps(candidate?.video),
              // ENG-4833: how the camera was identified + which driver it runs on.
              ...resolveSingleAddIdentification({
                identification: snapshot.identification,
                modelId: candidate.modelId,
                model: manufacturer.config?.model,
                hasControl: Boolean(candidate.control)
              })
            });

            if (wasFirstCamera) {
              trackIntercomEvent(INTERCOM_EVENTS.FIRST_CAMERA_ADDED, {
                deviceId: id.toString(),
                deviceType: deviceData.video?.protocol?.toString() ?? '',
                brand: manufacturer?.defaultName ?? '',
                model: deviceData.modelId?.toString() ?? '',
                roomId: roomId.toString()
              });
            }
          } catch {
            /* Telemetry is not persistence. */
          }
          try {
            void broker
              .acquire(agentId)
              ?.emit('device.added', id)
              .catch(() => {});
          } catch {
            /* A synchronous notification failure is also non-fatal. */
          }
          if (!current()) return undefined;
          if (onboarding === undefined) χref(apiRef)?.close(true);
          return id;
        } catch (cause) {
          fail(stage !== 'pre-mutation', false, cause);
          return undefined;
        } finally {
          clearTimeout(timer);
          producer((draft) => {
            const attempt = draft.finalSave;
            if (attempt?.deviceId !== id) return;
            // Per settle-every-terminal-branch: raw ownership lasts until actual IO settles.
            attempt.rawPending = false;
            // A supersede that landed while raw IO was in flight resolves here: a pre-mutation
            // flight never sent anything, so clear it for a fresh add rather than stranding the
            // dialog; a post-mutation supersede stays superseded (its completion was fenced).
            if (attempt.status === 'superseded' && stage === 'pre-mutation') {
              // Assigning undefined (not `delete`) actually clears it under the store's shallow merge.
              (draft as { finalSave?: FinalSave | undefined }).finalSave = undefined;
              draft.previewPaused = false;
            }
          });
        }
      };
      const savedId = await Promise.race([work(), deadline]);
      // The visible attempt settles even when its raw IO still owns the save lock.
      emitSingleAdd(EVENT_NAMES.SINGLE_ADD_COMPLETED, savedId ? 'success' : 'error');
      return savedId;
    }
  );

  /**
   * Bulk-commit pipeline.
   *
   *   1. Snapshot `multiSelection` at click time. A local const inside
   *      `useLatestFn` closes over the *current* render's
   *      `store.source.multiSelection`, then the rest of the pipeline
   *      iterates the snapshot. Post-click selection mutations (the
   *      checkbox writes) cannot affect the in-flight batch.
   *   2. `runBulkCommit` does the work (pure async; testable in
   *      isolation).
   *   3. Modal close timing: MAX(allSettled, 300ms) — the 300ms floor
   *      mirrors the footer CTA dwell so the morph is always visible.
   *   4. Focus return: first new tile if `successCount > 0`; else the
   *      dialog's own trigger-element return (Radix default).
   *
   * Per `think-in-systems` (ai.knowledge/global): orchestration only.
   * `runBulkCommit` is the framework-agnostic core.
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  const runSubmission = useMemo(createBulkCommitRunner, []);
  // `prepared` (ENG-4940): the click-time rows with identified models applied
  // and any templates the pre-save step fetched (identification/bulk.ts). Absent
  // → the store's rows and templates, exactly as before.
  const executeBulkCommit = useLatestFn(async (prepared?: BulkSavePrepared) => {
    // PR-10 snapshot: closure-capture `multiSelection` + `discoveries` at
    // click time. The `store` reference is itself the latest render's
    // selector slice; each render's `useLatestFn` closes over the latest
    // `store`, so this line reads the value as of the click and freezes
    // it for the rest of the function body via local consts.
    // With `prepared`, the selection the identify step and the basic-controls
    // notice saw wins over the live one (see BulkSavePrepared.selectionIds).
    const selectionIds: Discovery['id'][] = [
      ...(prepared?.selectionIds ?? store.source.multiSelection)
    ];
    // Bug fix: prefer `bulkDiscoveries` (mirrored from selector.tsx —
    // the same collection the Table
    // renders rows from, with ids that match what the user actually
    // clicked). Fall back to legacy `discoveries` so single-add and
    // any caller not on the bulk path is unaffected. See
    // store/types.ts source.bulkDiscoveries doc for why the two
    // collections diverge.
    const discoveries: readonly Discovery[] =
      prepared?.discoveries ?? store.source.bulkDiscoveries ?? store.source.discoveries;
    // Bug 6 fix: per-modelId device-registry templates mirrored from
    // selector.tsx. Passed into
    // `runBulkCommit` so the known-model branch in `buildBulkCandidate`
    // can fire for fingerprint-matched RTSP sources — instead of falling
    // through to the generic RTSP branch and producing model="Other".
    // Empty object fallback keeps the pipeline working for callers that
    // never populated the mirror (single-add bypasses this slice).
    const templates = prepared?.templates ?? store.source.bulkTemplates ?? {};
    // Brand-detected resolutions mirrored from selector.tsx (extended
    // discovery). Threaded into `runBulkCommit` so the brand-detected
    // branch in `buildBulkCandidate` can fire for discoveries matched to a
    // brand fingerprint but no specific model. Empty-object fallback keeps
    // the pipeline unchanged for callers that never populated the mirror.
    const brandDetected = store.source.bulkBrandDetected ?? {};
    const roomId = store.roomId;
    // Closure-capture appId for the ONVIF probe transient-id generator.
    // Studio assigns appId once at provider init; reading at click-time
    // is safe but the read is here for parity with the other snapshots.
    const appId = store.appId;

    // Resolve auth token + billing capacity at click time (both can
    // change while the user is selecting checkboxes).
    const token = await getAccessTokenSilently();
    const slotsRemaining = remainingCameraSlots();

    // Generate the funnel batchId once at click time. Uses the same
    // `generateId` primitive `handleCommit` uses for device ids so
    // PostHog's UUID parsing stays consistent. The id is threaded
    // through every emit inside `runBulkCommit` so the funnel can join
    // `multi_add_started` → per-device events → `multi_add_completed`
    // on this single UUID.
    const batchId = generateId<Device['id']>().toString();

    // Clear the failed-devices slice at the start of every batch (PRD
    // §2.11) so residue from a prior batch doesn't pollute the new
    // run's failed-tile rendering. Slice is per-room (this Provider
    // re-mounts on room change) but the user can run multiple batches
    // inside one room without leaving.
    // Also clear the cancellation flag (PRD §2.10). Without this, a
    // stale flag set in a prior batch (Cancel clicked, then a fresh
    // "Add N Sources" click) would short-circuit the new run on its
    // first iteration with zero visible cause. Composed with
    // `clearFailedDevices` so both resets happen in one immer pass.
    producer((draft) => {
      clearFailedDevices(draft);
      clearCancellation(draft);
    });

    // Run the orchestration core. Returns when allSettled completes
    // (no internal dwell — wiring composes the timing below).
    const allSettledStart = performance.now();
    const result = await runBulkCommit({
      existingDevices: store.source.existing,
      selectionIds,
      discoveries,
      // Bug 6 fix — per-modelId templates threaded through so the
      // known-model branch can fire for fingerprint-matched discoveries.
      templates,
      // Extended discovery — brand-detected resolutions threaded through
      // so the brand-detected branch can fire for brand-only discoveries.
      brandDetected,
      // ENG-4833: brand names for the rows' detected_brand prop.
      brands: depot.getState().registry?.brands,
      roomId,
      studioAgentId,
      appId,
      token,
      slotsRemaining,
      addDevice,
      post,
      // Sequential ONVIF probe + prompt UX.
      // setStatus drives the store slice the scaffold subscribes to;
      // readTransients gives the probe helper read-only access to the
      // external.transients map provider.tsx populates from state-sync.
      setStatus: (next) =>
        producer((draft) => {
          draft.source.bulkAddStatus = next;
        }),
      readTransients: () => {
        const live = storeApi?.getState();
        return live?.external?.transients as Readonly<Record<string, unknown>> | undefined;
      },
      // Funnel-keyed batch identifier.
      batchId,
      // ENG-4950: the same device_add tracker single add uses.
      trackDeviceAdd: deviceLifecycleTracker.trackDeviceAddSuccess,
      // Wire the failed-device collection seam to the store's slice
      // (PRD §2.11). Inside `runBulkCommit`, every per-device failure
      // branch invokes this to surface a failed-tile placeholder.
      collectFailedDevice: (entry) =>
        producer((draft) => {
          addFailedDevice(draft, entry);
        }),
      // Wire the cancellation getter to the store (PRD §2.10). The
      // pipeline polls this at the top of every per-device
      // iteration; `getState()` gives a fresh read so a Cancel click
      // that fires mid-iteration is observed by the NEXT iter's guard
      // (already-in-flight step is allowed to settle — no kill,
      // per §2.10).
      cancellationRequested: () => storeApi?.getState().source.cancellationRequested === true,
      // Wire the auto-configured recorder to the depot's
      // `addAutoConfiguredField` mutator (Multi-Add Phase 2).
      //
      // `runBulkCommit` invokes this inside the Promise.allSettled
      // fulfilled branch (i.e. the rejection guard). It forwards
      // `r.value`, which equals `plan.newId` in every case — the
      // allSettled iteratee returns `newId` — but we use `r.value`
      // because doing so makes the rejection-guard relationship
      // textually obvious at the call site. On rejection nothing is
      // forwarded, so the slice never points at a non-existent device.
      //
      // Production path: `depot.getState().produce(...)` mirrors how
      // every other depot mutation lands from this file (see
      // `<Discovery>::recordSingleAddAutoConfigured` in
      // `source/discovery.tsx` — same shape, same boundary). Wrapped in
      // try/catch so a recording failure (e.g. depot proxy not
      // resolved yet during a fast Jest mount) does NOT break the
      // commit: the user's device-add is the load-bearing operation;
      // the label is informational. Per `verify-and-escalate`
      // (ai.knowledge/workflow) — recording failures are logged but
      // not rethrown.
      //
      // Slice contract: `addAutoConfiguredField` silently drops
      // `auth.*` paths and B2 sensitive paths
      // (`/password|secret|token|apikey|passcode/i`). The seam upstream
      // skips `auth.*` too (`recordAuto` in `runBulkCommit`); both
      // guards stay so the recording site is explicit about its
      // contract and the slice is defense-in-depth.
      //
      // Per `think-in-systems` (ai.knowledge/global) — the wire is a
      // React-side adapter; the slice is the framework-agnostic
      // primitive.
      //
      // Per `reference-rules-in-comments` (ai.knowledge/global).
      recordAutoConfigured: (deviceId, fieldPath, value) => {
        try {
          depot.getState().produce((draft) => {
            addAutoConfiguredField(draft, deviceId, fieldPath, value);
          });
        } catch (error) {
          logger.error(<Error>error);
        }
      }
    });

    // A batch that landed ≥1 tile is a commit-driven close, not a cancel
    // — mark it so the modal-close visibility handler does not emit
    // ADD_SOURCE_MODAL_CANCELLED.
    if (result.successCount > 0) {
      committedRef.current = true;
    }

    // ---- Modal close timing: MAX(allSettled, 300ms) ----
    //
    // Floor at 300ms so the user always sees the bulk-mode morph +
    // "Adding…" CTA. allSettled at 100ms → wait the remaining 200ms;
    // allSettled at 5s → close immediately (no-op delay).
    const elapsed = performance.now() - allSettledStart;
    const remaining = Math.max(0, BULK_CLOSE_DWELL_MS - elapsed);
    if (remaining > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, remaining));
    }

    // ---- Modal close + focus return ----
    scheduleBulkClose(result, () => {
      χref(apiRef)?.close(true);
    });

    // ---- Renderer reload for Iris-enabled links ----
    //
    // When the batch successfully linked one or more Iris-enabled
    // devices, mirror the single-add contract from
    // `add/source/dialogs/credentials-dialog.tsx::handleConfirm` —
    // fire `ElectronAPI.onReloadStudio()` (Electron path) +
    // `window.location.reload()` (universal renderer path). Without
    // the reload, the newly-joined agent appears in the studio's
    // agent fleet via state-sync but the sidebar's room view doesn't
    // pick up the cameras advertised by that agent until the user
    // refreshes manually (per user report after Iris-enabled bulk
    // add). The reload is deferred slightly (300ms) so the modal
    // close animation + toast (if any) get a frame to render — Iris-
    // enabled UX consistency.
    if (result.irisLinkedCount > 0) {
      setTimeout(() => {
        if (isElectronApp()) {
          ElectronAPI.onReloadStudio();
        }
        window.location.reload();
      }, 300);
    }

    return result;
  });

  // Claim synchronously before token acquisition; React's button state alone
  // cannot prevent two submissions in the same render from creating new IDs.
  const handleBulkCommit = useLatestFn((prepared?: BulkSavePrepared) =>
    runSubmission(() => executeBulkCommit(prepared))
  );

  // Confirm cancellation, if necessary.
  //
  // When bulk-mode is active with N>5 selections, prompt the user to
  // confirm discarding the batch before closing. The
  // `useBulkDiscardConfirmation` hook handles both branches internally
  // — N≤5 invokes the `continueClose` callback synchronously, so the
  // modal closes without a visible prompt; N>5 shows the discard dialog
  // and only invokes `continueClose` on user confirm. We
  // `event.preventDefault()` before delegating so Radix's default close
  // path doesn't race the prompt.
  //
  // Per `reference-rules-in-comments` (ai.knowledge/global) —
  // cancel-confirm contract.
  const handleBeforeClose = useLatestFn((event: Event) => {
    if (panel > Panel.Source) {
      event.preventDefault();

      handleCancel();

      return;
    }

    // Bulk-mode discard guard (panel=Source). The hook is a no-op when
    // bulkMode is off OR when N≤5, so single-add and small-batch flows
    // close exactly as they did before this wire-up.
    if (store.source.bulkMode && store.source.multiSelection.length > 0) {
      event.preventDefault();
      tryBulkDiscard(() => handleClose());
      return;
    }

    handleClose();
  });

  // Cleanup when dialog closes + add-source modal open/cancel analytics.
  //
  // The Dialog's `visibility` event is the single open/close seam for the
  // add-source flow (any entry — discovery, manual, sidebar +). On open we
  // emit ADD_SOURCE_MODAL_OPENED and reset the commit flag for this
  // session; on close we emit ADD_SOURCE_MODAL_CANCELLED ONLY when no
  // device was committed during this open (a commit-driven close is not a
  // cancel). Per `reference-rules-in-comments` (ai.knowledge/global).
  useMount(() =>
    χref(apiRef)?.on('visibility', (visible: boolean) => {
      if (!visible) {
        handleCleanup();
        if (!committedRef.current) {
          emit(EVENT_NAMES.ADD_SOURCE_MODAL_CANCELLED, { studioId: store.roomId?.toString() });
        }
      } else {
        committedRef.current = false;
        emit(EVENT_NAMES.ADD_SOURCE_MODAL_OPENED, { studioId: store.roomId?.toString() });
        closed.current = false;
        active.current = true;
        producer((draft) => {
          // A reopened dialog may start fresh only after the old raw owner settles.
          if (!draft.finalSave?.rawPending) {
            (draft as { finalSave?: FinalSave | undefined }).finalSave = undefined;
            draft.previewPaused = false;
          }
        });
      }
    })
  );

  return useLazyRef(() => ({
    handleBeforeClose,
    handleBulkCommit,
    handleCancel,
    handleClose,
    handleCommit
  })).current;
};
